import React from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useAppSelector, useAppDispatch } from '../../../../store';
import { DataElementContext } from '../../../../page-components/common/DataElementContext';

import { processComponentProps } from '@/page-components/utils/processComponentProps';

import './index.scss';
import StarImage from './assets/star.png';
import StarGlow from './assets/star-glow.png';
import WhiteGlow from './assets/white-glow.png';
import WhiteGlowCircle from './assets/white-glow-circle.png';
import useCountUp from './countup/useCountUp';

type MomentumWinAnimatedProps = {
  children: any;
  styleText: string;
  className: string;
  properties?: any;
};

const MomentumWinAnimated = (componentProps: MomentumWinAnimatedProps) => {
  let props = componentProps;

  const dataElementContext = React.useContext(DataElementContext);
  [props] = processComponentProps(props, dataElementContext);

  const { t } = useTranslation();

  const value = parseFloat(props?.properties?.value ? props?.properties?.value : 0);

  const countUp = useCountUp({
    begin: open,
    start: value / 3,
    end: value,
    duration: 1500,
    delay: 2000,
  });

  if (!props?.properties?.open) return null;

  return (
    <div className={`momentum-won-panel-svg anim ${props?.properties?.isDesktop ? 'desktop' : ''}`}>
      <svg
        id="ejzKihZF9pk1"
        className="anim"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="-85 0 800 550"
        shapeRendering="geometricPrecision"
        textRendering="geometricPrecision"
      >
        <defs>
          <filter id="ejzKihZF9pk2-filter" x="-150%" width="400%" y="-150%" height="400%">
            <feGaussianBlur id="ejzKihZF9pk2-filter-drop-shadow-0-blur" in="SourceAlpha" stdDeviation="10,10" />
            <feOffset id="ejzKihZF9pk2-filter-drop-shadow-0-offset" dx="0" dy="10" result="tmp" />
            <feFlood id="ejzKihZF9pk2-filter-drop-shadow-0-flood" floodColor="#000" />
            <feComposite id="ejzKihZF9pk2-filter-drop-shadow-0-composite" operator="in" in2="tmp" />
            <feMerge id="ejzKihZF9pk2-filter-drop-shadow-0-merge" result="result">
              <feMergeNode id="ejzKihZF9pk2-filter-drop-shadow-0-merge-node-1" />
              <feMergeNode id="ejzKihZF9pk2-filter-drop-shadow-0-merge-node-2" in="SourceGraphic" />
            </feMerge>
          </filter>
          <radialGradient
            id="ejzKihZF9pk6-fill"
            cx="0"
            cy="0"
            r="1"
            spreadMethod="pad"
            gradientUnits="userSpaceOnUse"
            gradientTransform="matrix(213.558 0 0 213.329 370.52 250.438)"
          >
            <stop id="ejzKihZF9pk6-fill-0" offset="0%" stopColor="#c261e9" />
            <stop id="ejzKihZF9pk6-fill-1" offset="9%" stopColor="rgba(186,93,223,0.9216)" />
            <stop id="ejzKihZF9pk6-fill-2" offset="24%" stopColor="rgba(165,82,198,0.7225)" />
            <stop id="ejzKihZF9pk6-fill-3" offset="43%" stopColor="rgba(130,65,156,0.4489)" />
            <stop id="ejzKihZF9pk6-fill-4" offset="66%" stopColor="rgba(82,41,99,0.1849)" />
            <stop id="ejzKihZF9pk6-fill-5" offset="91%" stopColor="rgba(22,11,27,0.0144)" />
            <stop id="ejzKihZF9pk6-fill-6" offset="100%" stopColor="rgba(0,0,0,0)" />
          </radialGradient>
          <radialGradient
            id="ejzKihZF9pk7-fill"
            cx="0"
            cy="0"
            r="1"
            spreadMethod="pad"
            gradientUnits="userSpaceOnUse"
            gradientTransform="matrix(213.663 0 0 213.329 370.499 250.454)"
          >
            <stop id="ejzKihZF9pk7-fill-0" offset="0%" stopColor="#0043e9" />
            <stop id="ejzKihZF9pk7-fill-1" offset="23%" stopColor="rgba(0,47,166,0.5041)" />
            <stop id="ejzKihZF9pk7-fill-2" offset="51%" stopColor="rgba(0,27,95,0.1681)" />
            <stop id="ejzKihZF9pk7-fill-3" offset="73%" stopColor="rgba(0,12,43,0.0361)" />
            <stop id="ejzKihZF9pk7-fill-4" offset="91%" stopColor="rgba(0,3,12,0.01)" />
            <stop id="ejzKihZF9pk7-fill-5" offset="100%" stopColor="rgba(0,0,0,0)" />
          </radialGradient>
          <radialGradient
            id="ejzKihZF9pk8-fill"
            cx="0"
            cy="0"
            r="1"
            spreadMethod="pad"
            gradientUnits="userSpaceOnUse"
            gradientTransform="matrix(0 -108 108.001 0 373.001 275)"
          >
            <stop id="ejzKihZF9pk8-fill-0" offset="0%" stopColor="#3721a1" />
            <stop id="ejzKihZF9pk8-fill-1" offset="52.3392%" stopColor="rgba(53,135,153,0.41)" />
            <stop id="ejzKihZF9pk8-fill-2" offset="98.2656%" stopColor="rgba(53,129,153,0)" />
          </radialGradient>
          <filter id="ejzKihZF9pk35-filter" x="-150%" width="400%" y="-150%" height="400%">
            <feGaussianBlur id="ejzKihZF9pk35-filter-inner-shadow-0-blur" in="SourceAlpha" stdDeviation="3.35,3.35" />
            <feOffset id="ejzKihZF9pk35-filter-inner-shadow-0-offset" dx="0" dy="25" result="tmp" />
            <feComposite
              id="ejzKihZF9pk35-filter-inner-shadow-0-composite"
              operator="arithmetic"
              k2="-1"
              k3="1"
              in2="SourceGraphic"
            />
            <feColorMatrix
              id="ejzKihZF9pk35-filter-inner-shadow-0-color-matrix"
              type="matrix"
              values="0 0 0 0 0.070588 0 0 0 0 0.011765 0 0 0 0 0.184314 0 0 0 1 0"
            />
            <feMerge id="ejzKihZF9pk35-filter-inner-shadow-0-merge" result="result">
              <feMergeNode id="ejzKihZF9pk35-filter-inner-shadow-0-merge-node-1" in="SourceGraphic" />
              <feMergeNode id="ejzKihZF9pk35-filter-inner-shadow-0-merge-node-2" />
            </feMerge>
            <feGaussianBlur
              id="ejzKihZF9pk35-filter-inner-shadow-1-blur"
              in="result"
              stdDeviation="0.859305,0.859305"
            />
            <feOffset id="ejzKihZF9pk35-filter-inner-shadow-1-offset" dx="0" dy="-2" result="tmp" />
            <feComposite
              id="ejzKihZF9pk35-filter-inner-shadow-1-composite"
              operator="arithmetic"
              k2="-1"
              k3="1"
              in2="SourceGraphic"
            />
            <feColorMatrix
              id="ejzKihZF9pk35-filter-inner-shadow-1-color-matrix"
              type="matrix"
              values="0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.25 0"
            />
            <feMerge id="ejzKihZF9pk35-filter-inner-shadow-1-merge" result="result">
              <feMergeNode id="ejzKihZF9pk35-filter-inner-shadow-1-merge-node-1" in="result" />
              <feMergeNode id="ejzKihZF9pk35-filter-inner-shadow-1-merge-node-2" />
            </feMerge>
          </filter>
          <linearGradient
            id="ejzKihZF9pk36-fill"
            x1="180"
            y1="13.824"
            x2="180"
            y2="145.154"
            spreadMethod="pad"
            gradientUnits="userSpaceOnUse"
            gradientTransform="translate(0 0)"
          >
            <stop id="ejzKihZF9pk36-fill-0" offset="100%" stopColor="#6916a8" />
            <stop id="ejzKihZF9pk36-fill-1" offset="0%" stopColor="#081134" />
          </linearGradient>
          <linearGradient
            id="ejzKihZF9pk37-stroke"
            x1="171"
            y1="-1"
            x2="171"
            y2="131"
            spreadMethod="pad"
            gradientUnits="userSpaceOnUse"
            gradientTransform="translate(0 0)"
          >
            <stop id="ejzKihZF9pk37-stroke-0" offset="0%" stopColor="rgba(255,255,255,0)" />
            <stop id="ejzKihZF9pk37-stroke-1" offset="60%" stopColor="rgba(255,255,255,0.133804)" />
            <stop id="ejzKihZF9pk37-stroke-2" offset="75%" stopColor="#fff" />
            <stop id="ejzKihZF9pk37-stroke-3" offset="95%" stopColor="rgba(255,255,255,0.151938)" />
          </linearGradient>
          <filter id="ejzKihZF9pk45-filter" x="-150%" width="400%" y="-150%" height="400%">
            <feGaussianBlur id="ejzKihZF9pk45-filter-inner-shadow-0-blur" in="SourceAlpha" stdDeviation="1.75,1.75" />
            <feOffset id="ejzKihZF9pk45-filter-inner-shadow-0-offset" dx="0" dy="0" result="tmp" />
            <feComposite
              id="ejzKihZF9pk45-filter-inner-shadow-0-composite"
              operator="arithmetic"
              k2="-1"
              k3="1"
              in2="SourceGraphic"
            />
            <feColorMatrix
              id="ejzKihZF9pk45-filter-inner-shadow-0-color-matrix"
              type="matrix"
              values="0 0 0 0 0 0 0 0 0 0.215686 0 0 0 0 0.466667 0 0 0 1 0"
            />
            <feMerge id="ejzKihZF9pk45-filter-inner-shadow-0-merge" result="result">
              <feMergeNode id="ejzKihZF9pk45-filter-inner-shadow-0-merge-node-1" in="SourceGraphic" />
              <feMergeNode id="ejzKihZF9pk45-filter-inner-shadow-0-merge-node-2" />
            </feMerge>
            <feGaussianBlur
              id="ejzKihZF9pk45-filter-inner-shadow-1-blur"
              in="result"
              stdDeviation="0.859305,0.859305"
            />
            <feOffset id="ejzKihZF9pk45-filter-inner-shadow-1-offset" dx="0" dy="2.86435" result="tmp" />
            <feComposite
              id="ejzKihZF9pk45-filter-inner-shadow-1-composite"
              operator="arithmetic"
              k2="-1"
              k3="1"
              in2="SourceGraphic"
            />
            <feColorMatrix
              id="ejzKihZF9pk45-filter-inner-shadow-1-color-matrix"
              type="matrix"
              values="0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.25 0"
            />
            <feMerge id="ejzKihZF9pk45-filter-inner-shadow-1-merge" result="result">
              <feMergeNode id="ejzKihZF9pk45-filter-inner-shadow-1-merge-node-1" in="result" />
              <feMergeNode id="ejzKihZF9pk45-filter-inner-shadow-1-merge-node-2" />
            </feMerge>
          </filter>
          <radialGradient
            id="ejzKihZF9pk46-fill"
            cx="0"
            cy="0"
            r="1"
            spreadMethod="pad"
            gradientUnits="userSpaceOnUse"
            gradientTransform="matrix(0 72.5926 -234.521 0 370 192)"
          >
            <stop id="ejzKihZF9pk46-fill-0" offset="0%" stopColor="#fff" />
            <stop id="ejzKihZF9pk46-fill-1" offset="15%" stopColor="#ae89ff" />
            <stop id="ejzKihZF9pk46-fill-2" offset="35%" stopColor="#8400ed" />
            <stop id="ejzKihZF9pk46-fill-3" offset="97.1024%" stopColor="#2f0d50" />
          </radialGradient>
          <filter id="ejzKihZF9pk51-filter" x="-150%" width="400%" y="-150%" height="400%">
            <feGaussianBlur id="ejzKihZF9pk51-filter-drop-shadow-0-blur" in="SourceAlpha" stdDeviation="1.25,1.25" />
            <feOffset id="ejzKihZF9pk51-filter-drop-shadow-0-offset" dx="0" dy="4" result="tmp" />
            <feFlood id="ejzKihZF9pk51-filter-drop-shadow-0-flood" floodColor="#000" />
            <feComposite id="ejzKihZF9pk51-filter-drop-shadow-0-composite" operator="in" in2="tmp" />
            <feMerge id="ejzKihZF9pk51-filter-drop-shadow-0-merge" result="result">
              <feMergeNode id="ejzKihZF9pk51-filter-drop-shadow-0-merge-node-1" />
              <feMergeNode id="ejzKihZF9pk51-filter-drop-shadow-0-merge-node-2" in="SourceGraphic" />
            </feMerge>
            <feGaussianBlur id="ejzKihZF9pk51-filter-drop-shadow-1-blur" in="result" stdDeviation="15.6506,15.6506" />
            <feOffset id="ejzKihZF9pk51-filter-drop-shadow-1-offset" dx="0" dy="0" result="tmp" />
            <feFlood id="ejzKihZF9pk51-filter-drop-shadow-1-flood" floodColor="#a71cb3" />
            <feComposite id="ejzKihZF9pk51-filter-drop-shadow-1-composite" operator="in" in2="tmp" />
            <feMerge id="ejzKihZF9pk51-filter-drop-shadow-1-merge" result="result">
              <feMergeNode id="ejzKihZF9pk51-filter-drop-shadow-1-merge-node-1" />
              <feMergeNode id="ejzKihZF9pk51-filter-drop-shadow-1-merge-node-2" in="result" />
            </feMerge>
          </filter>
          <filter id="ejzKihZF9pk55-filter" x="-150%" width="400%" y="-150%" height="400%">
            <feGaussianBlur
              id="ejzKihZF9pk55-filter-inner-shadow-0-blur"
              in="SourceAlpha"
              stdDeviation="0.24952,0.24952"
            />
            <feOffset id="ejzKihZF9pk55-filter-inner-shadow-0-offset" dx="0" dy="-0.49904" result="tmp" />
            <feComposite
              id="ejzKihZF9pk55-filter-inner-shadow-0-composite"
              operator="arithmetic"
              k2="-1"
              k3="1"
              in2="SourceGraphic"
            />
            <feColorMatrix
              id="ejzKihZF9pk55-filter-inner-shadow-0-color-matrix"
              type="matrix"
              values="0 0 0 0 0 0 0 0 0 0.392157 0 0 0 0 0.039216 0 0 0 1 0"
            />
            <feMerge id="ejzKihZF9pk55-filter-inner-shadow-0-merge" result="result">
              <feMergeNode id="ejzKihZF9pk55-filter-inner-shadow-0-merge-node-1" in="SourceGraphic" />
              <feMergeNode id="ejzKihZF9pk55-filter-inner-shadow-0-merge-node-2" />
            </feMerge>
            <feGaussianBlur
              id="ejzKihZF9pk55-filter-inner-shadow-1-blur"
              in="result"
              stdDeviation="0.149712,0.149712"
            />
            <feOffset id="ejzKihZF9pk55-filter-inner-shadow-1-offset" dx="0" dy="0.49904" result="tmp" />
            <feComposite
              id="ejzKihZF9pk55-filter-inner-shadow-1-composite"
              operator="arithmetic"
              k2="-1"
              k3="1"
              in2="SourceGraphic"
            />
            <feColorMatrix
              id="ejzKihZF9pk55-filter-inner-shadow-1-color-matrix"
              type="matrix"
              values="0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.25 0"
            />
            <feMerge id="ejzKihZF9pk55-filter-inner-shadow-1-merge" result="result">
              <feMergeNode id="ejzKihZF9pk55-filter-inner-shadow-1-merge-node-1" in="result" />
              <feMergeNode id="ejzKihZF9pk55-filter-inner-shadow-1-merge-node-2" />
            </feMerge>
          </filter>
          <linearGradient
            id="ejzKihZF9pk56-fill"
            x1="526.905"
            y1="187.429"
            x2="542.967"
            y2="196.717"
            spreadMethod="pad"
            gradientUnits="userSpaceOnUse"
            gradientTransform="translate(0 0)"
          >
            <stop id="ejzKihZF9pk56-fill-0" offset="0%" stopColor="#26782e" />
            <stop id="ejzKihZF9pk56-fill-1" offset="100%" stopColor="#095010" />
          </linearGradient>
          <filter id="ejzKihZF9pk57-filter" x="-150%" width="400%" y="-150%" height="400%">
            <feGaussianBlur
              id="ejzKihZF9pk57-filter-inner-shadow-0-blur"
              in="SourceAlpha"
              stdDeviation="0.24952,0.24952"
            />
            <feOffset id="ejzKihZF9pk57-filter-inner-shadow-0-offset" dx="0" dy="-0.49904" result="tmp" />
            <feComposite
              id="ejzKihZF9pk57-filter-inner-shadow-0-composite"
              operator="arithmetic"
              k2="-1"
              k3="1"
              in2="SourceGraphic"
            />
            <feColorMatrix
              id="ejzKihZF9pk57-filter-inner-shadow-0-color-matrix"
              type="matrix"
              values="0 0 0 0 0 0 0 0 0 0.537255 0 0 0 0 0.439216 0 0 0 0.66 0"
            />
            <feMerge id="ejzKihZF9pk57-filter-inner-shadow-0-merge" result="result">
              <feMergeNode id="ejzKihZF9pk57-filter-inner-shadow-0-merge-node-1" in="SourceGraphic" />
              <feMergeNode id="ejzKihZF9pk57-filter-inner-shadow-0-merge-node-2" />
            </feMerge>
            <feGaussianBlur
              id="ejzKihZF9pk57-filter-inner-shadow-1-blur"
              in="result"
              stdDeviation="0.149712,0.149712"
            />
            <feOffset id="ejzKihZF9pk57-filter-inner-shadow-1-offset" dx="0" dy="0.49904" result="tmp" />
            <feComposite
              id="ejzKihZF9pk57-filter-inner-shadow-1-composite"
              operator="arithmetic"
              k2="-1"
              k3="1"
              in2="SourceGraphic"
            />
            <feColorMatrix
              id="ejzKihZF9pk57-filter-inner-shadow-1-color-matrix"
              type="matrix"
              values="0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.25 0"
            />
            <feMerge id="ejzKihZF9pk57-filter-inner-shadow-1-merge" result="result">
              <feMergeNode id="ejzKihZF9pk57-filter-inner-shadow-1-merge-node-1" in="result" />
              <feMergeNode id="ejzKihZF9pk57-filter-inner-shadow-1-merge-node-2" />
            </feMerge>
          </filter>
          <linearGradient
            id="ejzKihZF9pk58-fill"
            x1="520.27"
            y1="184.082"
            x2="541.044"
            y2="196.095"
            spreadMethod="pad"
            gradientUnits="userSpaceOnUse"
            gradientTransform="translate(0 0)"
          >
            <stop id="ejzKihZF9pk58-fill-0" offset="0%" stopColor="#8bdc93" />
            <stop id="ejzKihZF9pk58-fill-1" offset="100%" stopColor="#90de98" />
          </linearGradient>
          <filter id="ejzKihZF9pk59-filter" x="-150%" width="400%" y="-150%" height="400%">
            <feGaussianBlur
              id="ejzKihZF9pk59-filter-inner-shadow-0-blur"
              in="SourceAlpha"
              stdDeviation="0.24952,0.24952"
            />
            <feOffset id="ejzKihZF9pk59-filter-inner-shadow-0-offset" dx="0" dy="-0.49904" result="tmp" />
            <feComposite
              id="ejzKihZF9pk59-filter-inner-shadow-0-composite"
              operator="arithmetic"
              k2="-1"
              k3="1"
              in2="SourceGraphic"
            />
            <feColorMatrix
              id="ejzKihZF9pk59-filter-inner-shadow-0-color-matrix"
              type="matrix"
              values="0 0 0 0 0.14902 0 0 0 0 0.470588 0 0 0 0 0.180392 0 0 0 1 0"
            />
            <feMerge id="ejzKihZF9pk59-filter-inner-shadow-0-merge" result="result">
              <feMergeNode id="ejzKihZF9pk59-filter-inner-shadow-0-merge-node-1" in="SourceGraphic" />
              <feMergeNode id="ejzKihZF9pk59-filter-inner-shadow-0-merge-node-2" />
            </feMerge>
            <feGaussianBlur
              id="ejzKihZF9pk59-filter-inner-shadow-1-blur"
              in="result"
              stdDeviation="0.149712,0.149712"
            />
            <feOffset id="ejzKihZF9pk59-filter-inner-shadow-1-offset" dx="0" dy="0.49904" result="tmp" />
            <feComposite
              id="ejzKihZF9pk59-filter-inner-shadow-1-composite"
              operator="arithmetic"
              k2="-1"
              k3="1"
              in2="SourceGraphic"
            />
            <feColorMatrix
              id="ejzKihZF9pk59-filter-inner-shadow-1-color-matrix"
              type="matrix"
              values="0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.25 0"
            />
            <feMerge id="ejzKihZF9pk59-filter-inner-shadow-1-merge" result="result">
              <feMergeNode id="ejzKihZF9pk59-filter-inner-shadow-1-merge-node-1" in="result" />
              <feMergeNode id="ejzKihZF9pk59-filter-inner-shadow-1-merge-node-2" />
            </feMerge>
            <feGaussianBlur id="ejzKihZF9pk59-filter-drop-shadow-0-blur" in="result" stdDeviation="0.99808,0.99808" />
            <feOffset id="ejzKihZF9pk59-filter-drop-shadow-0-offset" dx="0" dy="1.99616" result="tmp" />
            <feFlood id="ejzKihZF9pk59-filter-drop-shadow-0-flood" floodColor="#000" />
            <feComposite id="ejzKihZF9pk59-filter-drop-shadow-0-composite" operator="in" in2="tmp" />
            <feMerge id="ejzKihZF9pk59-filter-drop-shadow-0-merge" result="result">
              <feMergeNode id="ejzKihZF9pk59-filter-drop-shadow-0-merge-node-1" />
              <feMergeNode id="ejzKihZF9pk59-filter-drop-shadow-0-merge-node-2" in="result" />
            </feMerge>
          </filter>
          <filter id="ejzKihZF9pk62-filter" x="-150%" width="400%" y="-150%" height="400%">
            <feGaussianBlur
              id="ejzKihZF9pk62-filter-inner-shadow-0-blur"
              in="SourceAlpha"
              stdDeviation="0.24952,0.24952"
            />
            <feOffset id="ejzKihZF9pk62-filter-inner-shadow-0-offset" dx="0" dy="-0.49904" result="tmp" />
            <feComposite
              id="ejzKihZF9pk62-filter-inner-shadow-0-composite"
              operator="arithmetic"
              k2="-1"
              k3="1"
              in2="SourceGraphic"
            />
            <feColorMatrix
              id="ejzKihZF9pk62-filter-inner-shadow-0-color-matrix"
              type="matrix"
              values="0 0 0 0 0.14902 0 0 0 0 0.470588 0 0 0 0 0.180392 0 0 0 0.58 0"
            />
            <feMerge id="ejzKihZF9pk62-filter-inner-shadow-0-merge" result="result">
              <feMergeNode id="ejzKihZF9pk62-filter-inner-shadow-0-merge-node-1" in="SourceGraphic" />
              <feMergeNode id="ejzKihZF9pk62-filter-inner-shadow-0-merge-node-2" />
            </feMerge>
          </filter>
          <linearGradient
            id="ejzKihZF9pk63-fill"
            x1="0"
            y1="-2.90513"
            x2="0"
            y2="2.90513"
            spreadMethod="pad"
            gradientUnits="userSpaceOnUse"
            gradientTransform="translate(0 0)"
          >
            <stop id="ejzKihZF9pk63-fill-0" offset="0%" stopColor="#6fd279" />
            <stop id="ejzKihZF9pk63-fill-1" offset="100%" stopColor="#99e0a0" />
          </linearGradient>
          <filter id="ejzKihZF9pk65-filter" x="-150%" width="400%" y="-150%" height="400%">
            <feGaussianBlur
              id="ejzKihZF9pk65-filter-inner-shadow-0-blur"
              in="SourceAlpha"
              stdDeviation="0.24952,0.24952"
            />
            <feOffset id="ejzKihZF9pk65-filter-inner-shadow-0-offset" dx="0" dy="-0.49904" result="tmp" />
            <feComposite
              id="ejzKihZF9pk65-filter-inner-shadow-0-composite"
              operator="arithmetic"
              k2="-1"
              k3="1"
              in2="SourceGraphic"
            />
            <feColorMatrix
              id="ejzKihZF9pk65-filter-inner-shadow-0-color-matrix"
              type="matrix"
              values="0 0 0 0 0 0 0 0 0 0.392157 0 0 0 0 0.039216 0 0 0 1 0"
            />
            <feMerge id="ejzKihZF9pk65-filter-inner-shadow-0-merge" result="result">
              <feMergeNode id="ejzKihZF9pk65-filter-inner-shadow-0-merge-node-1" in="SourceGraphic" />
              <feMergeNode id="ejzKihZF9pk65-filter-inner-shadow-0-merge-node-2" />
            </feMerge>
            <feGaussianBlur
              id="ejzKihZF9pk65-filter-inner-shadow-1-blur"
              in="result"
              stdDeviation="0.149712,0.149712"
            />
            <feOffset id="ejzKihZF9pk65-filter-inner-shadow-1-offset" dx="0" dy="0.49904" result="tmp" />
            <feComposite
              id="ejzKihZF9pk65-filter-inner-shadow-1-composite"
              operator="arithmetic"
              k2="-1"
              k3="1"
              in2="SourceGraphic"
            />
            <feColorMatrix
              id="ejzKihZF9pk65-filter-inner-shadow-1-color-matrix"
              type="matrix"
              values="0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.25 0"
            />
            <feMerge id="ejzKihZF9pk65-filter-inner-shadow-1-merge" result="result">
              <feMergeNode id="ejzKihZF9pk65-filter-inner-shadow-1-merge-node-1" in="result" />
              <feMergeNode id="ejzKihZF9pk65-filter-inner-shadow-1-merge-node-2" />
            </feMerge>
          </filter>
          <linearGradient
            id="ejzKihZF9pk66-fill"
            x1="200.931"
            y1="245.585"
            x2="187.811"
            y2="258.705"
            spreadMethod="pad"
            gradientUnits="userSpaceOnUse"
            gradientTransform="translate(0 0)"
          >
            <stop id="ejzKihZF9pk66-fill-0" offset="0%" stopColor="#26782e" />
            <stop id="ejzKihZF9pk66-fill-1" offset="100%" stopColor="#095010" />
          </linearGradient>
          <filter id="ejzKihZF9pk67-filter" x="-150%" width="400%" y="-150%" height="400%">
            <feGaussianBlur
              id="ejzKihZF9pk67-filter-inner-shadow-0-blur"
              in="SourceAlpha"
              stdDeviation="0.24952,0.24952"
            />
            <feOffset id="ejzKihZF9pk67-filter-inner-shadow-0-offset" dx="0" dy="-0.49904" result="tmp" />
            <feComposite
              id="ejzKihZF9pk67-filter-inner-shadow-0-composite"
              operator="arithmetic"
              k2="-1"
              k3="1"
              in2="SourceGraphic"
            />
            <feColorMatrix
              id="ejzKihZF9pk67-filter-inner-shadow-0-color-matrix"
              type="matrix"
              values="0 0 0 0 0 0 0 0 0 0.537255 0 0 0 0 0.439216 0 0 0 0.66 0"
            />
            <feMerge id="ejzKihZF9pk67-filter-inner-shadow-0-merge" result="result">
              <feMergeNode id="ejzKihZF9pk67-filter-inner-shadow-0-merge-node-1" in="SourceGraphic" />
              <feMergeNode id="ejzKihZF9pk67-filter-inner-shadow-0-merge-node-2" />
            </feMerge>
            <feGaussianBlur
              id="ejzKihZF9pk67-filter-inner-shadow-1-blur"
              in="result"
              stdDeviation="0.149712,0.149712"
            />
            <feOffset id="ejzKihZF9pk67-filter-inner-shadow-1-offset" dx="0" dy="0.49904" result="tmp" />
            <feComposite
              id="ejzKihZF9pk67-filter-inner-shadow-1-composite"
              operator="arithmetic"
              k2="-1"
              k3="1"
              in2="SourceGraphic"
            />
            <feColorMatrix
              id="ejzKihZF9pk67-filter-inner-shadow-1-color-matrix"
              type="matrix"
              values="0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.25 0"
            />
            <feMerge id="ejzKihZF9pk67-filter-inner-shadow-1-merge" result="result">
              <feMergeNode id="ejzKihZF9pk67-filter-inner-shadow-1-merge-node-1" in="result" />
              <feMergeNode id="ejzKihZF9pk67-filter-inner-shadow-1-merge-node-2" />
            </feMerge>
          </filter>
          <linearGradient
            id="ejzKihZF9pk68-fill"
            x1="205.877"
            y1="240.037"
            x2="188.909"
            y2="257.006"
            spreadMethod="pad"
            gradientUnits="userSpaceOnUse"
            gradientTransform="translate(0 0)"
          >
            <stop id="ejzKihZF9pk68-fill-0" offset="0%" stopColor="#8bdc93" />
            <stop id="ejzKihZF9pk68-fill-1" offset="100%" stopColor="#90de98" />
          </linearGradient>
          <filter id="ejzKihZF9pk69-filter" x="-150%" width="400%" y="-150%" height="400%">
            <feGaussianBlur
              id="ejzKihZF9pk69-filter-inner-shadow-0-blur"
              in="SourceAlpha"
              stdDeviation="0.24952,0.24952"
            />
            <feOffset id="ejzKihZF9pk69-filter-inner-shadow-0-offset" dx="0" dy="-0.49904" result="tmp" />
            <feComposite
              id="ejzKihZF9pk69-filter-inner-shadow-0-composite"
              operator="arithmetic"
              k2="-1"
              k3="1"
              in2="SourceGraphic"
            />
            <feColorMatrix
              id="ejzKihZF9pk69-filter-inner-shadow-0-color-matrix"
              type="matrix"
              values="0 0 0 0 0.14902 0 0 0 0 0.470588 0 0 0 0 0.180392 0 0 0 1 0"
            />
            <feMerge id="ejzKihZF9pk69-filter-inner-shadow-0-merge" result="result">
              <feMergeNode id="ejzKihZF9pk69-filter-inner-shadow-0-merge-node-1" in="SourceGraphic" />
              <feMergeNode id="ejzKihZF9pk69-filter-inner-shadow-0-merge-node-2" />
            </feMerge>
            <feGaussianBlur
              id="ejzKihZF9pk69-filter-inner-shadow-1-blur"
              in="result"
              stdDeviation="0.149712,0.149712"
            />
            <feOffset id="ejzKihZF9pk69-filter-inner-shadow-1-offset" dx="0" dy="0.49904" result="tmp" />
            <feComposite
              id="ejzKihZF9pk69-filter-inner-shadow-1-composite"
              operator="arithmetic"
              k2="-1"
              k3="1"
              in2="SourceGraphic"
            />
            <feColorMatrix
              id="ejzKihZF9pk69-filter-inner-shadow-1-color-matrix"
              type="matrix"
              values="0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.25 0"
            />
            <feMerge id="ejzKihZF9pk69-filter-inner-shadow-1-merge" result="result">
              <feMergeNode id="ejzKihZF9pk69-filter-inner-shadow-1-merge-node-1" in="result" />
              <feMergeNode id="ejzKihZF9pk69-filter-inner-shadow-1-merge-node-2" />
            </feMerge>
            <feGaussianBlur id="ejzKihZF9pk69-filter-drop-shadow-0-blur" in="result" stdDeviation="0.99808,0.99808" />
            <feOffset id="ejzKihZF9pk69-filter-drop-shadow-0-offset" dx="0" dy="1.99616" result="tmp" />
            <feFlood id="ejzKihZF9pk69-filter-drop-shadow-0-flood" floodColor="#000" />
            <feComposite id="ejzKihZF9pk69-filter-drop-shadow-0-composite" operator="in" in2="tmp" />
            <feMerge id="ejzKihZF9pk69-filter-drop-shadow-0-merge" result="result">
              <feMergeNode id="ejzKihZF9pk69-filter-drop-shadow-0-merge-node-1" />
              <feMergeNode id="ejzKihZF9pk69-filter-drop-shadow-0-merge-node-2" in="result" />
            </feMerge>
          </filter>
          <filter id="ejzKihZF9pk72-filter" x="-150%" width="400%" y="-150%" height="400%">
            <feGaussianBlur
              id="ejzKihZF9pk72-filter-inner-shadow-0-blur"
              in="SourceAlpha"
              stdDeviation="0.24952,0.24952"
            />
            <feOffset id="ejzKihZF9pk72-filter-inner-shadow-0-offset" dx="0" dy="-0.49904" result="tmp" />
            <feComposite
              id="ejzKihZF9pk72-filter-inner-shadow-0-composite"
              operator="arithmetic"
              k2="-1"
              k3="1"
              in2="SourceGraphic"
            />
            <feColorMatrix
              id="ejzKihZF9pk72-filter-inner-shadow-0-color-matrix"
              type="matrix"
              values="0 0 0 0 0.14902 0 0 0 0 0.470588 0 0 0 0 0.180392 0 0 0 0.58 0"
            />
            <feMerge id="ejzKihZF9pk72-filter-inner-shadow-0-merge" result="result">
              <feMergeNode id="ejzKihZF9pk72-filter-inner-shadow-0-merge-node-1" in="SourceGraphic" />
              <feMergeNode id="ejzKihZF9pk72-filter-inner-shadow-0-merge-node-2" />
            </feMerge>
          </filter>
          <linearGradient
            id="ejzKihZF9pk73-fill"
            x1="0"
            y1="-2.90513"
            x2="0"
            y2="2.90513"
            spreadMethod="pad"
            gradientUnits="userSpaceOnUse"
            gradientTransform="translate(0 0)"
          >
            <stop id="ejzKihZF9pk73-fill-0" offset="0%" stopColor="#6fd279" />
            <stop id="ejzKihZF9pk73-fill-1" offset="100%" stopColor="#99e0a0" />
          </linearGradient>
          <filter id="ejzKihZF9pk75-filter" x="-150%" width="400%" y="-150%" height="400%">
            <feGaussianBlur
              id="ejzKihZF9pk75-filter-inner-shadow-0-blur"
              in="SourceAlpha"
              stdDeviation="1.43218,1.43218"
            />
            <feOffset id="ejzKihZF9pk75-filter-inner-shadow-0-offset" dx="0" dy="-2.86435" result="tmp" />
            <feComposite
              id="ejzKihZF9pk75-filter-inner-shadow-0-composite"
              operator="arithmetic"
              k2="-1"
              k3="1"
              in2="SourceGraphic"
            />
            <feColorMatrix
              id="ejzKihZF9pk75-filter-inner-shadow-0-color-matrix"
              type="matrix"
              values="0 0 0 0 0 0 0 0 0 0.215686 0 0 0 0 0.466667 0 0 0 1 0"
            />
            <feMerge id="ejzKihZF9pk75-filter-inner-shadow-0-merge" result="result">
              <feMergeNode id="ejzKihZF9pk75-filter-inner-shadow-0-merge-node-1" in="SourceGraphic" />
              <feMergeNode id="ejzKihZF9pk75-filter-inner-shadow-0-merge-node-2" />
            </feMerge>
            <feGaussianBlur
              id="ejzKihZF9pk75-filter-inner-shadow-1-blur"
              in="result"
              stdDeviation="0.859305,0.859305"
            />
            <feOffset id="ejzKihZF9pk75-filter-inner-shadow-1-offset" dx="0" dy="2.86435" result="tmp" />
            <feComposite
              id="ejzKihZF9pk75-filter-inner-shadow-1-composite"
              operator="arithmetic"
              k2="-1"
              k3="1"
              in2="SourceGraphic"
            />
            <feColorMatrix
              id="ejzKihZF9pk75-filter-inner-shadow-1-color-matrix"
              type="matrix"
              values="0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.25 0"
            />
            <feMerge id="ejzKihZF9pk75-filter-inner-shadow-1-merge" result="result">
              <feMergeNode id="ejzKihZF9pk75-filter-inner-shadow-1-merge-node-1" in="result" />
              <feMergeNode id="ejzKihZF9pk75-filter-inner-shadow-1-merge-node-2" />
            </feMerge>
          </filter>
          <linearGradient
            id="ejzKihZF9pk76-fill"
            x1="214.694"
            y1="32.0062"
            x2="214.694"
            y2="120.664"
            spreadMethod="pad"
            gradientUnits="userSpaceOnUse"
            gradientTransform="translate(0 0)"
          >
            <stop id="ejzKihZF9pk76-fill-0" offset="0%" stopColor="#6916a8" />
            <stop id="ejzKihZF9pk76-fill-1" offset="100%" stopColor="#081134" />
          </linearGradient>
          <filter id="ejzKihZF9pk77-filter" x="-150%" width="400%" y="-150%" height="400%">
            <feGaussianBlur
              id="ejzKihZF9pk77-filter-drop-shadow-0-blur"
              in="SourceAlpha"
              stdDeviation="3.59531,3.59531"
            />
            <feOffset id="ejzKihZF9pk77-filter-drop-shadow-0-offset" dx="0" dy="0" result="tmp" />
            <feFlood id="ejzKihZF9pk77-filter-drop-shadow-0-flood" floodColor="#b31c9b" />
            <feComposite id="ejzKihZF9pk77-filter-drop-shadow-0-composite" operator="in" in2="tmp" />
            <feMerge id="ejzKihZF9pk77-filter-drop-shadow-0-merge" result="result">
              <feMergeNode id="ejzKihZF9pk77-filter-drop-shadow-0-merge-node-1" />
              <feMergeNode id="ejzKihZF9pk77-filter-drop-shadow-0-merge-node-2" in="SourceGraphic" />
            </feMerge>
          </filter>
          <linearGradient
            id="ejzKihZF9pk80-fill"
            x1="215.032"
            y1="37.4818"
            x2="215.032"
            y2="76.4816"
            spreadMethod="pad"
            gradientUnits="userSpaceOnUse"
            gradientTransform="translate(0 0)"
          >
            <stop id="ejzKihZF9pk80-fill-0" offset="0%" stopColor="#f94848" />
            <stop id="ejzKihZF9pk80-fill-1" offset="100%" stopColor="#330363" />
          </linearGradient>
          <filter id="ejzKihZF9pk84-filter" x="-150%" width="400%" y="-150%" height="400%">
            <feGaussianBlur
              id="ejzKihZF9pk84-filter-inner-shadow-0-blur"
              in="SourceAlpha"
              stdDeviation="0.881918,0.881918"
            />
            <feOffset id="ejzKihZF9pk84-filter-inner-shadow-0-offset" dx="0" dy="2.93973" result="tmp" />
            <feComposite
              id="ejzKihZF9pk84-filter-inner-shadow-0-composite"
              operator="arithmetic"
              k2="-1"
              k3="1"
              in2="SourceGraphic"
            />
            <feColorMatrix
              id="ejzKihZF9pk84-filter-inner-shadow-0-color-matrix"
              type="matrix"
              values="0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.25 0"
            />
            <feMerge id="ejzKihZF9pk84-filter-inner-shadow-0-merge" result="result">
              <feMergeNode id="ejzKihZF9pk84-filter-inner-shadow-0-merge-node-1" in="SourceGraphic" />
              <feMergeNode id="ejzKihZF9pk84-filter-inner-shadow-0-merge-node-2" />
            </feMerge>
            <feGaussianBlur id="ejzKihZF9pk84-filter-drop-shadow-0-blur" in="result" stdDeviation="2.05781,2.05781" />
            <feOffset id="ejzKihZF9pk84-filter-drop-shadow-0-offset" dx="0" dy="5.87946" result="tmp" />
            <feFlood id="ejzKihZF9pk84-filter-drop-shadow-0-flood" floodColor="#000" />
            <feComposite id="ejzKihZF9pk84-filter-drop-shadow-0-composite" operator="in" in2="tmp" />
            <feMerge id="ejzKihZF9pk84-filter-drop-shadow-0-merge" result="result">
              <feMergeNode id="ejzKihZF9pk84-filter-drop-shadow-0-merge-node-1" />
              <feMergeNode id="ejzKihZF9pk84-filter-drop-shadow-0-merge-node-2" in="result" />
            </feMerge>
            <feGaussianBlur id="ejzKihZF9pk84-filter-drop-shadow-1-blur" in="result" stdDeviation="4.40959,4.40959" />
            <feOffset id="ejzKihZF9pk84-filter-drop-shadow-1-offset" dx="0" dy="0" result="tmp" />
            <feFlood id="ejzKihZF9pk84-filter-drop-shadow-1-flood" floodColor="#4beaff" />
            <feComposite id="ejzKihZF9pk84-filter-drop-shadow-1-composite" operator="in" in2="tmp" />
            <feMerge id="ejzKihZF9pk84-filter-drop-shadow-1-merge" result="result">
              <feMergeNode id="ejzKihZF9pk84-filter-drop-shadow-1-merge-node-1" />
              <feMergeNode id="ejzKihZF9pk84-filter-drop-shadow-1-merge-node-2" in="result" />
            </feMerge>
          </filter>
          <linearGradient
            id="ejzKihZF9pk85-fill"
            x1="214.156"
            y1="72.0453"
            x2="214.156"
            y2="105.077"
            spreadMethod="pad"
            gradientUnits="userSpaceOnUse"
            gradientTransform="translate(0 0)"
          >
            <stop id="ejzKihZF9pk85-fill-0" offset="0%" stopColor="#fff" />
            <stop id="ejzKihZF9pk85-fill-1" offset="100%" stopColor="#dfdfdf" />
          </linearGradient>
          <linearGradient
            id="ejzKihZF9pk87-fill"
            x1="214.156"
            y1="72.0453"
            x2="214.156"
            y2="103.641"
            spreadMethod="pad"
            gradientUnits="userSpaceOnUse"
            gradientTransform="translate(0 0)"
          >
            <stop id="ejzKihZF9pk87-fill-0" offset="0%" stopColor="#ededed" />
            <stop id="ejzKihZF9pk87-fill-1" offset="100%" stopColor="#fff" />
          </linearGradient>
          <filter id="ejzKihZF9pk91-filter" x="-150%" width="400%" y="-150%" height="400%">
            <feGaussianBlur
              id="ejzKihZF9pk91-filter-inner-shadow-0-blur"
              in="SourceAlpha"
              stdDeviation="0.666667,0.666667"
            />
            <feOffset id="ejzKihZF9pk91-filter-inner-shadow-0-offset" dx="-1.33333" dy="0" result="tmp" />
            <feComposite
              id="ejzKihZF9pk91-filter-inner-shadow-0-composite"
              operator="arithmetic"
              k2="-1"
              k3="1"
              in2="SourceGraphic"
            />
            <feColorMatrix
              id="ejzKihZF9pk91-filter-inner-shadow-0-color-matrix"
              type="matrix"
              values="0 0 0 0 0.815686 0 0 0 0 0.513725 0 0 0 0 0 0 0 0 1 0"
            />
            <feMerge id="ejzKihZF9pk91-filter-inner-shadow-0-merge" result="result">
              <feMergeNode id="ejzKihZF9pk91-filter-inner-shadow-0-merge-node-1" in="SourceGraphic" />
              <feMergeNode id="ejzKihZF9pk91-filter-inner-shadow-0-merge-node-2" />
            </feMerge>
          </filter>
          <filter id="ejzKihZF9pk93-filter" x="-150%" width="400%" y="-150%" height="400%">
            <feGaussianBlur
              id="ejzKihZF9pk93-filter-inner-shadow-0-blur"
              in="SourceAlpha"
              stdDeviation="0.666667,0.666667"
            />
            <feOffset id="ejzKihZF9pk93-filter-inner-shadow-0-offset" dx="-1.33333" dy="0" result="tmp" />
            <feComposite
              id="ejzKihZF9pk93-filter-inner-shadow-0-composite"
              operator="arithmetic"
              k2="-1"
              k3="1"
              in2="SourceGraphic"
            />
            <feColorMatrix
              id="ejzKihZF9pk93-filter-inner-shadow-0-color-matrix"
              type="matrix"
              values="0 0 0 0 0.815686 0 0 0 0 0.513725 0 0 0 0 0 0 0 0 1 0"
            />
            <feMerge id="ejzKihZF9pk93-filter-inner-shadow-0-merge" result="result">
              <feMergeNode id="ejzKihZF9pk93-filter-inner-shadow-0-merge-node-1" in="SourceGraphic" />
              <feMergeNode id="ejzKihZF9pk93-filter-inner-shadow-0-merge-node-2" />
            </feMerge>
          </filter>
          <filter id="ejzKihZF9pk95-filter" x="-150%" width="400%" y="-150%" height="400%">
            <feGaussianBlur
              id="ejzKihZF9pk95-filter-inner-shadow-0-blur"
              in="SourceAlpha"
              stdDeviation="0.666667,0.666667"
            />
            <feOffset id="ejzKihZF9pk95-filter-inner-shadow-0-offset" dx="-1.33333" dy="0" result="tmp" />
            <feComposite
              id="ejzKihZF9pk95-filter-inner-shadow-0-composite"
              operator="arithmetic"
              k2="-1"
              k3="1"
              in2="SourceGraphic"
            />
            <feColorMatrix
              id="ejzKihZF9pk95-filter-inner-shadow-0-color-matrix"
              type="matrix"
              values="0 0 0 0 0.815686 0 0 0 0 0.513725 0 0 0 0 0 0 0 0 1 0"
            />
            <feMerge id="ejzKihZF9pk95-filter-inner-shadow-0-merge" result="result">
              <feMergeNode id="ejzKihZF9pk95-filter-inner-shadow-0-merge-node-1" in="SourceGraphic" />
              <feMergeNode id="ejzKihZF9pk95-filter-inner-shadow-0-merge-node-2" />
            </feMerge>
          </filter>
          <filter id="ejzKihZF9pk97-filter" x="-150%" width="400%" y="-150%" height="400%">
            <feGaussianBlur
              id="ejzKihZF9pk97-filter-inner-shadow-0-blur"
              in="SourceAlpha"
              stdDeviation="0.666667,0.666667"
            />
            <feOffset id="ejzKihZF9pk97-filter-inner-shadow-0-offset" dx="-1.33333" dy="0" result="tmp" />
            <feComposite
              id="ejzKihZF9pk97-filter-inner-shadow-0-composite"
              operator="arithmetic"
              k2="-1"
              k3="1"
              in2="SourceGraphic"
            />
            <feColorMatrix
              id="ejzKihZF9pk97-filter-inner-shadow-0-color-matrix"
              type="matrix"
              values="0 0 0 0 0.815686 0 0 0 0 0.513725 0 0 0 0 0 0 0 0 1 0"
            />
            <feMerge id="ejzKihZF9pk97-filter-inner-shadow-0-merge" result="result">
              <feMergeNode id="ejzKihZF9pk97-filter-inner-shadow-0-merge-node-1" in="SourceGraphic" />
              <feMergeNode id="ejzKihZF9pk97-filter-inner-shadow-0-merge-node-2" />
            </feMerge>
          </filter>
          <filter id="ejzKihZF9pk99-filter" x="-150%" width="400%" y="-150%" height="400%">
            <feGaussianBlur
              id="ejzKihZF9pk99-filter-inner-shadow-0-blur"
              in="SourceAlpha"
              stdDeviation="0.666667,0.666667"
            />
            <feOffset id="ejzKihZF9pk99-filter-inner-shadow-0-offset" dx="-1.33333" dy="0" result="tmp" />
            <feComposite
              id="ejzKihZF9pk99-filter-inner-shadow-0-composite"
              operator="arithmetic"
              k2="-1"
              k3="1"
              in2="SourceGraphic"
            />
            <feColorMatrix
              id="ejzKihZF9pk99-filter-inner-shadow-0-color-matrix"
              type="matrix"
              values="0 0 0 0 0.815686 0 0 0 0 0.513725 0 0 0 0 0 0 0 0 1 0"
            />
            <feMerge id="ejzKihZF9pk99-filter-inner-shadow-0-merge" result="result">
              <feMergeNode id="ejzKihZF9pk99-filter-inner-shadow-0-merge-node-1" in="SourceGraphic" />
              <feMergeNode id="ejzKihZF9pk99-filter-inner-shadow-0-merge-node-2" />
            </feMerge>
          </filter>
          <filter id="ejzKihZF9pk102-filter" x="-150%" width="400%" y="-150%" height="400%">
            <feGaussianBlur
              id="ejzKihZF9pk102-filter-inner-shadow-0-blur"
              in="SourceAlpha"
              stdDeviation="0.24952,0.24952"
            />
            <feOffset id="ejzKihZF9pk102-filter-inner-shadow-0-offset" dx="0" dy="-0.49904" result="tmp" />
            <feComposite
              id="ejzKihZF9pk102-filter-inner-shadow-0-composite"
              operator="arithmetic"
              k2="-1"
              k3="1"
              in2="SourceGraphic"
            />
            <feColorMatrix
              id="ejzKihZF9pk102-filter-inner-shadow-0-color-matrix"
              type="matrix"
              values="0 0 0 0 0 0 0 0 0 0.392157 0 0 0 0 0.039216 0 0 0 1 0"
            />
            <feMerge id="ejzKihZF9pk102-filter-inner-shadow-0-merge" result="result">
              <feMergeNode id="ejzKihZF9pk102-filter-inner-shadow-0-merge-node-1" in="SourceGraphic" />
              <feMergeNode id="ejzKihZF9pk102-filter-inner-shadow-0-merge-node-2" />
            </feMerge>
            <feGaussianBlur
              id="ejzKihZF9pk102-filter-inner-shadow-1-blur"
              in="result"
              stdDeviation="0.149712,0.149712"
            />
            <feOffset id="ejzKihZF9pk102-filter-inner-shadow-1-offset" dx="0" dy="0.49904" result="tmp" />
            <feComposite
              id="ejzKihZF9pk102-filter-inner-shadow-1-composite"
              operator="arithmetic"
              k2="-1"
              k3="1"
              in2="SourceGraphic"
            />
            <feColorMatrix
              id="ejzKihZF9pk102-filter-inner-shadow-1-color-matrix"
              type="matrix"
              values="0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.25 0"
            />
            <feMerge id="ejzKihZF9pk102-filter-inner-shadow-1-merge" result="result">
              <feMergeNode id="ejzKihZF9pk102-filter-inner-shadow-1-merge-node-1" in="result" />
              <feMergeNode id="ejzKihZF9pk102-filter-inner-shadow-1-merge-node-2" />
            </feMerge>
          </filter>
          <linearGradient
            id="ejzKihZF9pk103-fill"
            x1="160.775"
            y1="81.0453"
            x2="151.727"
            y2="88.4497"
            spreadMethod="pad"
            gradientUnits="userSpaceOnUse"
            gradientTransform="translate(0 0)"
          >
            <stop id="ejzKihZF9pk103-fill-0" offset="0%" stopColor="#26782e" />
            <stop id="ejzKihZF9pk103-fill-1" offset="100%" stopColor="#095010" />
          </linearGradient>
          <filter id="ejzKihZF9pk104-filter" x="-150%" width="400%" y="-150%" height="400%">
            <feGaussianBlur
              id="ejzKihZF9pk104-filter-inner-shadow-0-blur"
              in="SourceAlpha"
              stdDeviation="0.24952,0.24952"
            />
            <feOffset id="ejzKihZF9pk104-filter-inner-shadow-0-offset" dx="0" dy="-0.49904" result="tmp" />
            <feComposite
              id="ejzKihZF9pk104-filter-inner-shadow-0-composite"
              operator="arithmetic"
              k2="-1"
              k3="1"
              in2="SourceGraphic"
            />
            <feColorMatrix
              id="ejzKihZF9pk104-filter-inner-shadow-0-color-matrix"
              type="matrix"
              values="0 0 0 0 0 0 0 0 0 0.537255 0 0 0 0 0.439216 0 0 0 0.66 0"
            />
            <feMerge id="ejzKihZF9pk104-filter-inner-shadow-0-merge" result="result">
              <feMergeNode id="ejzKihZF9pk104-filter-inner-shadow-0-merge-node-1" in="SourceGraphic" />
              <feMergeNode id="ejzKihZF9pk104-filter-inner-shadow-0-merge-node-2" />
            </feMerge>
            <feGaussianBlur
              id="ejzKihZF9pk104-filter-inner-shadow-1-blur"
              in="result"
              stdDeviation="0.149712,0.149712"
            />
            <feOffset id="ejzKihZF9pk104-filter-inner-shadow-1-offset" dx="0" dy="0.49904" result="tmp" />
            <feComposite
              id="ejzKihZF9pk104-filter-inner-shadow-1-composite"
              operator="arithmetic"
              k2="-1"
              k3="1"
              in2="SourceGraphic"
            />
            <feColorMatrix
              id="ejzKihZF9pk104-filter-inner-shadow-1-color-matrix"
              type="matrix"
              values="0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.25 0"
            />
            <feMerge id="ejzKihZF9pk104-filter-inner-shadow-1-merge" result="result">
              <feMergeNode id="ejzKihZF9pk104-filter-inner-shadow-1-merge-node-1" in="result" />
              <feMergeNode id="ejzKihZF9pk104-filter-inner-shadow-1-merge-node-2" />
            </feMerge>
          </filter>
          <linearGradient
            id="ejzKihZF9pk105-fill"
            x1="164.565"
            y1="78.2916"
            x2="152.863"
            y2="87.8682"
            spreadMethod="pad"
            gradientUnits="userSpaceOnUse"
            gradientTransform="translate(0 0)"
          >
            <stop id="ejzKihZF9pk105-fill-0" offset="0%" stopColor="#8bdc93" />
            <stop id="ejzKihZF9pk105-fill-1" offset="100%" stopColor="#90de98" />
          </linearGradient>
          <filter id="ejzKihZF9pk106-filter" x="-150%" width="400%" y="-150%" height="400%">
            <feGaussianBlur
              id="ejzKihZF9pk106-filter-inner-shadow-0-blur"
              in="SourceAlpha"
              stdDeviation="0.24952,0.24952"
            />
            <feOffset id="ejzKihZF9pk106-filter-inner-shadow-0-offset" dx="0" dy="-0.49904" result="tmp" />
            <feComposite
              id="ejzKihZF9pk106-filter-inner-shadow-0-composite"
              operator="arithmetic"
              k2="-1"
              k3="1"
              in2="SourceGraphic"
            />
            <feColorMatrix
              id="ejzKihZF9pk106-filter-inner-shadow-0-color-matrix"
              type="matrix"
              values="0 0 0 0 0.14902 0 0 0 0 0.470588 0 0 0 0 0.180392 0 0 0 1 0"
            />
            <feMerge id="ejzKihZF9pk106-filter-inner-shadow-0-merge" result="result">
              <feMergeNode id="ejzKihZF9pk106-filter-inner-shadow-0-merge-node-1" in="SourceGraphic" />
              <feMergeNode id="ejzKihZF9pk106-filter-inner-shadow-0-merge-node-2" />
            </feMerge>
            <feGaussianBlur
              id="ejzKihZF9pk106-filter-inner-shadow-1-blur"
              in="result"
              stdDeviation="0.149712,0.149712"
            />
            <feOffset id="ejzKihZF9pk106-filter-inner-shadow-1-offset" dx="0" dy="0.49904" result="tmp" />
            <feComposite
              id="ejzKihZF9pk106-filter-inner-shadow-1-composite"
              operator="arithmetic"
              k2="-1"
              k3="1"
              in2="SourceGraphic"
            />
            <feColorMatrix
              id="ejzKihZF9pk106-filter-inner-shadow-1-color-matrix"
              type="matrix"
              values="0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.25 0"
            />
            <feMerge id="ejzKihZF9pk106-filter-inner-shadow-1-merge" result="result">
              <feMergeNode id="ejzKihZF9pk106-filter-inner-shadow-1-merge-node-1" in="result" />
              <feMergeNode id="ejzKihZF9pk106-filter-inner-shadow-1-merge-node-2" />
            </feMerge>
            <feGaussianBlur id="ejzKihZF9pk106-filter-drop-shadow-0-blur" in="result" stdDeviation="0.99808,0.99808" />
            <feOffset id="ejzKihZF9pk106-filter-drop-shadow-0-offset" dx="0" dy="1.99616" result="tmp" />
            <feFlood id="ejzKihZF9pk106-filter-drop-shadow-0-flood" floodColor="#000" />
            <feComposite id="ejzKihZF9pk106-filter-drop-shadow-0-composite" operator="in" in2="tmp" />
            <feMerge id="ejzKihZF9pk106-filter-drop-shadow-0-merge" result="result">
              <feMergeNode id="ejzKihZF9pk106-filter-drop-shadow-0-merge-node-1" />
              <feMergeNode id="ejzKihZF9pk106-filter-drop-shadow-0-merge-node-2" in="result" />
            </feMerge>
          </filter>
          <filter id="ejzKihZF9pk109-filter" x="-150%" width="400%" y="-150%" height="400%">
            <feGaussianBlur
              id="ejzKihZF9pk109-filter-inner-shadow-0-blur"
              in="SourceAlpha"
              stdDeviation="0.24952,0.24952"
            />
            <feOffset id="ejzKihZF9pk109-filter-inner-shadow-0-offset" dx="0" dy="-0.49904" result="tmp" />
            <feComposite
              id="ejzKihZF9pk109-filter-inner-shadow-0-composite"
              operator="arithmetic"
              k2="-1"
              k3="1"
              in2="SourceGraphic"
            />
            <feColorMatrix
              id="ejzKihZF9pk109-filter-inner-shadow-0-color-matrix"
              type="matrix"
              values="0 0 0 0 0.14902 0 0 0 0 0.470588 0 0 0 0 0.180392 0 0 0 0.58 0"
            />
            <feMerge id="ejzKihZF9pk109-filter-inner-shadow-0-merge" result="result">
              <feMergeNode id="ejzKihZF9pk109-filter-inner-shadow-0-merge-node-1" in="SourceGraphic" />
              <feMergeNode id="ejzKihZF9pk109-filter-inner-shadow-0-merge-node-2" />
            </feMerge>
          </filter>
          <linearGradient
            id="ejzKihZF9pk110-fill"
            x1="0"
            y1="-1.83079"
            x2="0"
            y2="1.8308"
            spreadMethod="pad"
            gradientUnits="userSpaceOnUse"
            gradientTransform="translate(0 0)"
          >
            <stop id="ejzKihZF9pk110-fill-0" offset="0%" stopColor="#6fd279" />
            <stop id="ejzKihZF9pk110-fill-1" offset="100%" stopColor="#99e0a0" />
          </linearGradient>
          <filter id="ejzKihZF9pk112-filter" x="-150%" width="400%" y="-150%" height="400%">
            <feGaussianBlur
              id="ejzKihZF9pk112-filter-inner-shadow-0-blur"
              in="SourceAlpha"
              stdDeviation="0.666667,0.666667"
            />
            <feOffset id="ejzKihZF9pk112-filter-inner-shadow-0-offset" dx="-1.33333" dy="0" result="tmp" />
            <feComposite
              id="ejzKihZF9pk112-filter-inner-shadow-0-composite"
              operator="arithmetic"
              k2="-1"
              k3="1"
              in2="SourceGraphic"
            />
            <feColorMatrix
              id="ejzKihZF9pk112-filter-inner-shadow-0-color-matrix"
              type="matrix"
              values="0 0 0 0 0.815686 0 0 0 0 0.513725 0 0 0 0 0 0 0 0 1 0"
            />
            <feMerge id="ejzKihZF9pk112-filter-inner-shadow-0-merge" result="result">
              <feMergeNode id="ejzKihZF9pk112-filter-inner-shadow-0-merge-node-1" in="SourceGraphic" />
              <feMergeNode id="ejzKihZF9pk112-filter-inner-shadow-0-merge-node-2" />
            </feMerge>
          </filter>
          <filter id="ejzKihZF9pk114-filter" x="-150%" width="400%" y="-150%" height="400%">
            <feGaussianBlur
              id="ejzKihZF9pk114-filter-inner-shadow-0-blur"
              in="SourceAlpha"
              stdDeviation="0.666667,0.666667"
            />
            <feOffset id="ejzKihZF9pk114-filter-inner-shadow-0-offset" dx="-1.33333" dy="0" result="tmp" />
            <feComposite
              id="ejzKihZF9pk114-filter-inner-shadow-0-composite"
              operator="arithmetic"
              k2="-1"
              k3="1"
              in2="SourceGraphic"
            />
            <feColorMatrix
              id="ejzKihZF9pk114-filter-inner-shadow-0-color-matrix"
              type="matrix"
              values="0 0 0 0 0.815686 0 0 0 0 0.513725 0 0 0 0 0 0 0 0 1 0"
            />
            <feMerge id="ejzKihZF9pk114-filter-inner-shadow-0-merge" result="result">
              <feMergeNode id="ejzKihZF9pk114-filter-inner-shadow-0-merge-node-1" in="SourceGraphic" />
              <feMergeNode id="ejzKihZF9pk114-filter-inner-shadow-0-merge-node-2" />
            </feMerge>
          </filter>
          <filter id="ejzKihZF9pk116-filter" x="-150%" width="400%" y="-150%" height="400%">
            <feGaussianBlur
              id="ejzKihZF9pk116-filter-inner-shadow-0-blur"
              in="SourceAlpha"
              stdDeviation="0.666667,0.666667"
            />
            <feOffset id="ejzKihZF9pk116-filter-inner-shadow-0-offset" dx="-1.33333" dy="0" result="tmp" />
            <feComposite
              id="ejzKihZF9pk116-filter-inner-shadow-0-composite"
              operator="arithmetic"
              k2="-1"
              k3="1"
              in2="SourceGraphic"
            />
            <feColorMatrix
              id="ejzKihZF9pk116-filter-inner-shadow-0-color-matrix"
              type="matrix"
              values="0 0 0 0 0.815686 0 0 0 0 0.513725 0 0 0 0 0 0 0 0 1 0"
            />
            <feMerge id="ejzKihZF9pk116-filter-inner-shadow-0-merge" result="result">
              <feMergeNode id="ejzKihZF9pk116-filter-inner-shadow-0-merge-node-1" in="SourceGraphic" />
              <feMergeNode id="ejzKihZF9pk116-filter-inner-shadow-0-merge-node-2" />
            </feMerge>
          </filter>
          <filter id="ejzKihZF9pk118-filter" x="-150%" width="400%" y="-150%" height="400%">
            <feGaussianBlur
              id="ejzKihZF9pk118-filter-inner-shadow-0-blur"
              in="SourceAlpha"
              stdDeviation="0.666667,0.666667"
            />
            <feOffset id="ejzKihZF9pk118-filter-inner-shadow-0-offset" dx="-1.33333" dy="0" result="tmp" />
            <feComposite
              id="ejzKihZF9pk118-filter-inner-shadow-0-composite"
              operator="arithmetic"
              k2="-1"
              k3="1"
              in2="SourceGraphic"
            />
            <feColorMatrix
              id="ejzKihZF9pk118-filter-inner-shadow-0-color-matrix"
              type="matrix"
              values="0 0 0 0 0.815686 0 0 0 0 0.513725 0 0 0 0 0 0 0 0 1 0"
            />
            <feMerge id="ejzKihZF9pk118-filter-inner-shadow-0-merge" result="result">
              <feMergeNode id="ejzKihZF9pk118-filter-inner-shadow-0-merge-node-1" in="SourceGraphic" />
              <feMergeNode id="ejzKihZF9pk118-filter-inner-shadow-0-merge-node-2" />
            </feMerge>
          </filter>
          <filter id="ejzKihZF9pk120-filter" x="-150%" width="400%" y="-150%" height="400%">
            <feGaussianBlur
              id="ejzKihZF9pk120-filter-inner-shadow-0-blur"
              in="SourceAlpha"
              stdDeviation="0.666667,0.666667"
            />
            <feOffset id="ejzKihZF9pk120-filter-inner-shadow-0-offset" dx="-1.33333" dy="0" result="tmp" />
            <feComposite
              id="ejzKihZF9pk120-filter-inner-shadow-0-composite"
              operator="arithmetic"
              k2="-1"
              k3="1"
              in2="SourceGraphic"
            />
            <feColorMatrix
              id="ejzKihZF9pk120-filter-inner-shadow-0-color-matrix"
              type="matrix"
              values="0 0 0 0 0.815686 0 0 0 0 0.513725 0 0 0 0 0 0 0 0 1 0"
            />
            <feMerge id="ejzKihZF9pk120-filter-inner-shadow-0-merge" result="result">
              <feMergeNode id="ejzKihZF9pk120-filter-inner-shadow-0-merge-node-1" in="SourceGraphic" />
              <feMergeNode id="ejzKihZF9pk120-filter-inner-shadow-0-merge-node-2" />
            </feMerge>
          </filter>
          <filter id="ejzKihZF9pk123-filter" x="-150%" width="400%" y="-150%" height="400%">
            <feGaussianBlur
              id="ejzKihZF9pk123-filter-inner-shadow-0-blur"
              in="SourceAlpha"
              stdDeviation="0.24952,0.24952"
            />
            <feOffset id="ejzKihZF9pk123-filter-inner-shadow-0-offset" dx="0" dy="-0.49904" result="tmp" />
            <feComposite
              id="ejzKihZF9pk123-filter-inner-shadow-0-composite"
              operator="arithmetic"
              k2="-1"
              k3="1"
              in2="SourceGraphic"
            />
            <feColorMatrix
              id="ejzKihZF9pk123-filter-inner-shadow-0-color-matrix"
              type="matrix"
              values="0 0 0 0 0 0 0 0 0 0.392157 0 0 0 0 0.039216 0 0 0 1 0"
            />
            <feMerge id="ejzKihZF9pk123-filter-inner-shadow-0-merge" result="result">
              <feMergeNode id="ejzKihZF9pk123-filter-inner-shadow-0-merge-node-1" in="SourceGraphic" />
              <feMergeNode id="ejzKihZF9pk123-filter-inner-shadow-0-merge-node-2" />
            </feMerge>
            <feGaussianBlur
              id="ejzKihZF9pk123-filter-inner-shadow-1-blur"
              in="result"
              stdDeviation="0.149712,0.149712"
            />
            <feOffset id="ejzKihZF9pk123-filter-inner-shadow-1-offset" dx="0" dy="0.49904" result="tmp" />
            <feComposite
              id="ejzKihZF9pk123-filter-inner-shadow-1-composite"
              operator="arithmetic"
              k2="-1"
              k3="1"
              in2="SourceGraphic"
            />
            <feColorMatrix
              id="ejzKihZF9pk123-filter-inner-shadow-1-color-matrix"
              type="matrix"
              values="0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.25 0"
            />
            <feMerge id="ejzKihZF9pk123-filter-inner-shadow-1-merge" result="result">
              <feMergeNode id="ejzKihZF9pk123-filter-inner-shadow-1-merge-node-1" in="result" />
              <feMergeNode id="ejzKihZF9pk123-filter-inner-shadow-1-merge-node-2" />
            </feMerge>
          </filter>
          <linearGradient
            id="ejzKihZF9pk124-fill"
            x1="268.255"
            y1="50.027"
            x2="278.378"
            y2="55.8808"
            spreadMethod="pad"
            gradientUnits="userSpaceOnUse"
            gradientTransform="translate(0 0)"
          >
            <stop id="ejzKihZF9pk124-fill-0" offset="0%" stopColor="#26782e" />
            <stop id="ejzKihZF9pk124-fill-1" offset="100%" stopColor="#095010" />
          </linearGradient>
          <filter id="ejzKihZF9pk125-filter" x="-150%" width="400%" y="-150%" height="400%">
            <feGaussianBlur
              id="ejzKihZF9pk125-filter-inner-shadow-0-blur"
              in="SourceAlpha"
              stdDeviation="0.24952,0.24952"
            />
            <feOffset id="ejzKihZF9pk125-filter-inner-shadow-0-offset" dx="0" dy="-0.49904" result="tmp" />
            <feComposite
              id="ejzKihZF9pk125-filter-inner-shadow-0-composite"
              operator="arithmetic"
              k2="-1"
              k3="1"
              in2="SourceGraphic"
            />
            <feColorMatrix
              id="ejzKihZF9pk125-filter-inner-shadow-0-color-matrix"
              type="matrix"
              values="0 0 0 0 0 0 0 0 0 0.537255 0 0 0 0 0.439216 0 0 0 0.66 0"
            />
            <feMerge id="ejzKihZF9pk125-filter-inner-shadow-0-merge" result="result">
              <feMergeNode id="ejzKihZF9pk125-filter-inner-shadow-0-merge-node-1" in="SourceGraphic" />
              <feMergeNode id="ejzKihZF9pk125-filter-inner-shadow-0-merge-node-2" />
            </feMerge>
            <feGaussianBlur
              id="ejzKihZF9pk125-filter-inner-shadow-1-blur"
              in="result"
              stdDeviation="0.149712,0.149712"
            />
            <feOffset id="ejzKihZF9pk125-filter-inner-shadow-1-offset" dx="0" dy="0.49904" result="tmp" />
            <feComposite
              id="ejzKihZF9pk125-filter-inner-shadow-1-composite"
              operator="arithmetic"
              k2="-1"
              k3="1"
              in2="SourceGraphic"
            />
            <feColorMatrix
              id="ejzKihZF9pk125-filter-inner-shadow-1-color-matrix"
              type="matrix"
              values="0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.25 0"
            />
            <feMerge id="ejzKihZF9pk125-filter-inner-shadow-1-merge" result="result">
              <feMergeNode id="ejzKihZF9pk125-filter-inner-shadow-1-merge-node-1" in="result" />
              <feMergeNode id="ejzKihZF9pk125-filter-inner-shadow-1-merge-node-2" />
            </feMerge>
          </filter>
          <linearGradient
            id="ejzKihZF9pk126-fill"
            x1="264.071"
            y1="47.9178"
            x2="277.164"
            y2="55.4889"
            spreadMethod="pad"
            gradientUnits="userSpaceOnUse"
            gradientTransform="translate(0 0)"
          >
            <stop id="ejzKihZF9pk126-fill-0" offset="0%" stopColor="#8bdc93" />
            <stop id="ejzKihZF9pk126-fill-1" offset="100%" stopColor="#90de98" />
          </linearGradient>
          <filter id="ejzKihZF9pk127-filter" x="-150%" width="400%" y="-150%" height="400%">
            <feGaussianBlur
              id="ejzKihZF9pk127-filter-inner-shadow-0-blur"
              in="SourceAlpha"
              stdDeviation="0.24952,0.24952"
            />
            <feOffset id="ejzKihZF9pk127-filter-inner-shadow-0-offset" dx="0" dy="-0.49904" result="tmp" />
            <feComposite
              id="ejzKihZF9pk127-filter-inner-shadow-0-composite"
              operator="arithmetic"
              k2="-1"
              k3="1"
              in2="SourceGraphic"
            />
            <feColorMatrix
              id="ejzKihZF9pk127-filter-inner-shadow-0-color-matrix"
              type="matrix"
              values="0 0 0 0 0.14902 0 0 0 0 0.470588 0 0 0 0 0.180392 0 0 0 1 0"
            />
            <feMerge id="ejzKihZF9pk127-filter-inner-shadow-0-merge" result="result">
              <feMergeNode id="ejzKihZF9pk127-filter-inner-shadow-0-merge-node-1" in="SourceGraphic" />
              <feMergeNode id="ejzKihZF9pk127-filter-inner-shadow-0-merge-node-2" />
            </feMerge>
            <feGaussianBlur
              id="ejzKihZF9pk127-filter-inner-shadow-1-blur"
              in="result"
              stdDeviation="0.149712,0.149712"
            />
            <feOffset id="ejzKihZF9pk127-filter-inner-shadow-1-offset" dx="0" dy="0.49904" result="tmp" />
            <feComposite
              id="ejzKihZF9pk127-filter-inner-shadow-1-composite"
              operator="arithmetic"
              k2="-1"
              k3="1"
              in2="SourceGraphic"
            />
            <feColorMatrix
              id="ejzKihZF9pk127-filter-inner-shadow-1-color-matrix"
              type="matrix"
              values="0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.25 0"
            />
            <feMerge id="ejzKihZF9pk127-filter-inner-shadow-1-merge" result="result">
              <feMergeNode id="ejzKihZF9pk127-filter-inner-shadow-1-merge-node-1" in="result" />
              <feMergeNode id="ejzKihZF9pk127-filter-inner-shadow-1-merge-node-2" />
            </feMerge>
            <feGaussianBlur id="ejzKihZF9pk127-filter-drop-shadow-0-blur" in="result" stdDeviation="0.99808,0.99808" />
            <feOffset id="ejzKihZF9pk127-filter-drop-shadow-0-offset" dx="0" dy="1.99616" result="tmp" />
            <feFlood id="ejzKihZF9pk127-filter-drop-shadow-0-flood" floodColor="#000" />
            <feComposite id="ejzKihZF9pk127-filter-drop-shadow-0-composite" operator="in" in2="tmp" />
            <feMerge id="ejzKihZF9pk127-filter-drop-shadow-0-merge" result="result">
              <feMergeNode id="ejzKihZF9pk127-filter-drop-shadow-0-merge-node-1" />
              <feMergeNode id="ejzKihZF9pk127-filter-drop-shadow-0-merge-node-2" in="result" />
            </feMerge>
          </filter>
          <filter id="ejzKihZF9pk130-filter" x="-150%" width="400%" y="-150%" height="400%">
            <feGaussianBlur
              id="ejzKihZF9pk130-filter-inner-shadow-0-blur"
              in="SourceAlpha"
              stdDeviation="0.24952,0.24952"
            />
            <feOffset id="ejzKihZF9pk130-filter-inner-shadow-0-offset" dx="0" dy="-0.49904" result="tmp" />
            <feComposite
              id="ejzKihZF9pk130-filter-inner-shadow-0-composite"
              operator="arithmetic"
              k2="-1"
              k3="1"
              in2="SourceGraphic"
            />
            <feColorMatrix
              id="ejzKihZF9pk130-filter-inner-shadow-0-color-matrix"
              type="matrix"
              values="0 0 0 0 0.14902 0 0 0 0 0.470588 0 0 0 0 0.180392 0 0 0 0.58 0"
            />
            <feMerge id="ejzKihZF9pk130-filter-inner-shadow-0-merge" result="result">
              <feMergeNode id="ejzKihZF9pk130-filter-inner-shadow-0-merge-node-1" in="SourceGraphic" />
              <feMergeNode id="ejzKihZF9pk130-filter-inner-shadow-0-merge-node-2" />
            </feMerge>
          </filter>
          <linearGradient
            id="ejzKihZF9pk131-fill"
            x1="0"
            y1="-1.83103"
            x2="0"
            y2="1.83102"
            spreadMethod="pad"
            gradientUnits="userSpaceOnUse"
            gradientTransform="translate(0 0)"
          >
            <stop id="ejzKihZF9pk131-fill-0" offset="0%" stopColor="#6fd279" />
            <stop id="ejzKihZF9pk131-fill-1" offset="100%" stopColor="#99e0a0" />
          </linearGradient>
        </defs>
        <g transform="translate(-56.001999 11.388092)" filter="url(#ejzKihZF9pk2-filter)">
          <g clipPath="url(#ejzKihZF9pk132)">
            <g>
              <g id="ejzKihZF9pk5" opacity="0.25">
                <path
                  style={{ mixBlendMode: 'color-dodge' }}
                  d="M586,250.5C586,132.587,490.189,37,372,37s-214,95.587-214,213.5s95.811,213.5,214,213.5s214-95.587,214-213.5Z"
                  opacity="0.47"
                  fill="url(#ejzKihZF9pk6-fill)"
                />
                <path
                  style={{ mixBlendMode: 'screen' }}
                  d="M586,250.5C586,132.587,490.189,37,372,37s-214,95.587-214,213.5s95.811,213.5,214,213.5s214-95.587,214-213.5Z"
                  opacity="0.68"
                  fill="url(#ejzKihZF9pk7-fill)"
                />
                <path
                  d="M402.001,125h-58l13.437,118.919-47.823-109.707-50.229,29l71.096,96.269-96.268-71.097-29,50.23l109.707,47.823L196,273v58l118.921-13.438-109.707,47.823l29,50.23l96.268-71.097-71.096,96.269l50.229,29L357.438,360.08L344.001,479h58L388.564,360.081l47.823,109.707l50.229-29-71.096-96.269l96.268,71.097l29-50.23-109.707-47.823L550.002,331v-58l-119.44,13.496l110.725-48.267-29-50.229-96.741,71.446l71.07-96.233-50.229-29L388.564,243.92L402.001,125Z"
                  fill="url(#ejzKihZF9pk8-fill)"
                />
              </g>
              <g transform="translate(.000001 0.000001)">
                <path
                  id="ejzKihZF9pk10"
                  d="M556.008,432l.037,4.592.856-4.512-.784,4.525l1.648-4.286-1.579,4.312l2.386-3.923-2.323,3.96l3.049-3.433-2.994,3.482l3.613-2.834-3.567,2.891l4.061-2.143-4.026,2.208l4.378-1.384-4.355,1.454l4.555-.58-4.546.653l4.586.242-4.589-.169l4.468,1.058-4.484-.986l4.207,1.838-4.236-1.771l3.812,2.56-3.853-2.498l3.294,3.199-3.344-3.146l2.669,3.736-2.729-3.693l1.959,4.153-2.025-4.121l1.186,4.436-1.257-4.417.375,4.577-.448-4.57-.448,4.57.375-4.577-1.257,4.417l1.186-4.436-2.026,4.121l1.96-4.153-2.729,3.693l2.669-3.736-3.344,3.146l3.293-3.199-3.852,2.498l3.812-2.56-4.237,1.771l4.208-1.838-4.485.986l4.469-1.058-4.589.169l4.585-.242-4.545-.653l4.555.58-4.355-1.454l4.378,1.384-4.026-2.208l4.061,2.143-3.567-2.891l3.612,2.834-2.993-3.482l3.049,3.433-2.324-3.96l2.387,3.923-1.579-4.312l1.648,4.286-.784-4.525.856,4.512.037-4.592Z"
                  transform="matrix(.8 0 0 0.8 172.202-190.602)"
                  opacity="0"
                  fill="#fff"
                />
                <path
                  id="ejzKihZF9pk11"
                  d="M508.008,112l.037,4.592.856-4.512-.784,4.525l1.648-4.286-1.579,4.312l2.386-3.923-2.323,3.96l3.049-3.433-2.994,3.482l3.613-2.834-3.567,2.891l4.061-2.143-4.026,2.208l4.378-1.384-4.355,1.454l4.555-.58-4.546.653l4.586.242-4.589-.169l4.468,1.058-4.484-.986l4.207,1.838-4.236-1.771l3.812,2.56-3.853-2.498l3.294,3.199-3.344-3.146l2.669,3.736-2.729-3.693l1.959,4.153-2.025-4.121l1.186,4.436-1.257-4.417.375,4.577-.448-4.57-.448,4.57.375-4.577-1.257,4.417l1.186-4.436-2.026,4.121l1.96-4.153-2.729,3.693l2.669-3.736-3.344,3.146l3.293-3.199-3.852,2.498l3.812-2.56-4.237,1.771l4.208-1.838-4.485.986l4.469-1.058-4.589.169l4.585-.242-4.545-.653l4.555.58-4.355-1.454l4.378,1.384-4.026-2.208l4.061,2.143-3.567-2.891l3.612,2.834-2.993-3.482l3.049,3.433-2.324-3.96l2.387,3.923-1.579-4.312l1.648,4.286-.784-4.525.856,4.512.037-4.592Z"
                  transform="translate(-24-48)"
                  opacity="0"
                  fill="#fff"
                />
                <path
                  id="ejzKihZF9pk12"
                  d="M195.008,84l.037,4.5917.856-4.5113-.784,4.5244l1.648-4.286-1.579,4.3118l2.386-3.9228-2.323,3.9605l3.049-3.4337-2.994,3.4821l3.613-2.8341-3.567,2.8915l4.061-2.1434-4.026,2.2082l4.378-1.384-4.355,1.4539l4.555-.58-4.546.6528l4.586.2427-4.589-.1693l4.468,1.0576-4.484-.9859l4.207,1.8384-4.236-1.7708l3.812,2.5602-3.853-2.4989l3.294,3.1997-3.344-3.1465l2.669,3.7363-2.729-3.6931l1.959,4.1528-2.025-4.1209l1.186,4.4359-1.257-4.4164.375,4.5765-.448-4.5699-.448,4.5699.375-4.5765-1.257,4.4164l1.186-4.4359-2.026,4.1209l1.96-4.1528-2.729,3.6931l2.669-3.7363-3.344,3.1465l3.293-3.1997-3.852,2.4989l3.812-2.5602-4.237,1.7708l4.208-1.8384-4.485.9859l4.469-1.0576-4.589.1693l4.585-.2427-4.545-.6528l4.555.58-4.355-1.4539l4.378,1.384-4.026-2.2082l4.061,2.1434-3.567-2.8915l3.612,2.8341-2.993-3.4821l3.049,3.4337-2.324-3.9605l2.387,3.9228-1.579-4.3118l1.648,4.286-.784-4.5244.856,4.5113L195.008,84Z"
                  transform="matrix(.8 0 0 0.8 117.002 48.798)"
                  opacity="0"
                  fill="#fff"
                />
                <path
                  id="ejzKihZF9pk13"
                  d="M556.008,432l.037,4.592.856-4.512-.784,4.525l1.648-4.286-1.579,4.312l2.386-3.923-2.323,3.96l3.049-3.433-2.994,3.482l3.613-2.834-3.567,2.891l4.061-2.143-4.026,2.208l4.378-1.384-4.355,1.454l4.555-.58-4.546.653l4.586.242-4.589-.169l4.468,1.058-4.484-.986l4.207,1.838-4.236-1.771l3.812,2.56-3.853-2.498l3.294,3.199-3.344-3.146l2.669,3.736-2.729-3.693l1.959,4.153-2.025-4.121l1.186,4.436-1.257-4.417.375,4.577-.448-4.57-.448,4.57.375-4.577-1.257,4.417l1.186-4.436-2.026,4.121l1.96-4.153-2.729,3.693l2.669-3.736-3.344,3.146l3.293-3.199-3.852,2.498l3.812-2.56-4.237,1.771l4.208-1.838-4.485.986l4.469-1.058-4.589.169l4.585-.242-4.545-.653l4.555.58-4.355-1.454l4.378,1.384-4.026-2.208l4.061,2.143-3.567-2.891l3.612,2.834-2.993-3.482l3.049,3.433-2.324-3.96l2.387,3.923-1.579-4.312l1.648,4.286-.784-4.525.856,4.512.037-4.592Z"
                  transform="matrix(.9 0 0 0.9-29.3992-222.301)"
                  opacity="0"
                  fill="#fff"
                />
                <path
                  id="ejzKihZF9pk14"
                  d="M212.008,131l.037,4.592.856-4.512-.784,4.525l1.648-4.286-1.579,4.312l2.386-3.923-2.323,3.96l3.049-3.433-2.994,3.482l3.613-2.834-3.567,2.891l4.061-2.143-4.026,2.208l4.378-1.384-4.355,1.454l4.555-.58-4.546.653l4.586.242-4.589-.169l4.468,1.058-4.484-.986l4.207,1.838-4.236-1.771l3.812,2.56-3.853-2.498l3.294,3.199-3.344-3.146l2.669,3.736-2.729-3.693l1.959,4.153-2.025-4.121l1.186,4.436-1.257-4.417.375,4.577-.448-4.57-.448,4.57.375-4.577-1.257,4.417l1.186-4.436-2.026,4.121l1.96-4.153-2.729,3.693l2.669-3.736-3.344,3.146l3.293-3.199-3.852,2.498l3.812-2.56-4.237,1.771l4.208-1.838-4.485.986l4.469-1.058-4.589.169l4.585-.242-4.545-.653l4.555.58-4.355-1.454l4.378,1.384-4.026-2.208l4.061,2.143-3.567-2.891l3.612,2.834-2.993-3.482l3.049,3.433-2.324-3.96l2.387,3.923-1.579-4.312l1.648,4.286-.784-4.525.856,4.512.037-4.592Z"
                  transform="matrix(1.2 0 0 1.2 35.5984 3.802)"
                  opacity="0"
                  fill="#fff"
                />
                <path
                  id="ejzKihZF9pk15"
                  d="M212.008,131l.037,4.592.856-4.512-.784,4.525l1.648-4.286-1.579,4.312l2.386-3.923-2.323,3.96l3.049-3.433-2.994,3.482l3.613-2.834-3.567,2.891l4.061-2.143-4.026,2.208l4.378-1.384-4.355,1.454l4.555-.58-4.546.653l4.586.242-4.589-.169l4.468,1.058-4.484-.986l4.207,1.838-4.236-1.771l3.812,2.56-3.853-2.498l3.294,3.199-3.344-3.146l2.669,3.736-2.729-3.693l1.959,4.153-2.025-4.121l1.186,4.436-1.257-4.417.375,4.577-.448-4.57-.448,4.57.375-4.577-1.257,4.417l1.186-4.436-2.026,4.121l1.96-4.153-2.729,3.693l2.669-3.736-3.344,3.146l3.293-3.199-3.852,2.498l3.812-2.56-4.237,1.771l4.208-1.838-4.485.986l4.469-1.058-4.589.169l4.585-.242-4.545-.653l4.555.58-4.355-1.454l4.378,1.384-4.026-2.208l4.061,2.143-3.567-2.891l3.612,2.834-2.993-3.482l3.049,3.433-2.324-3.96l2.387,3.923-1.579-4.312l1.648,4.286-.784-4.525.856,4.512.037-4.592Z"
                  transform="translate(-23 306)"
                  opacity="0"
                  fill="#fff"
                />
                <path
                  id="ejzKihZF9pk16"
                  d="M538.008,211l.037,4.592.856-4.512-.784,4.525l1.648-4.286-1.579,4.312l2.386-3.923-2.323,3.96l3.049-3.433-2.994,3.482l3.613-2.834-3.567,2.891l4.061-2.143-4.026,2.208l4.378-1.384-4.355,1.454l4.555-.58-4.546.653l4.586.242-4.589-.169l4.468,1.058-4.484-.986l4.207,1.838-4.236-1.771l3.812,2.56-3.853-2.498l3.294,3.199-3.344-3.146l2.669,3.736-2.729-3.693l1.959,4.153-2.025-4.121l1.186,4.436-1.257-4.417.375,4.577-.448-4.57-.448,4.57.375-4.577-1.257,4.417l1.186-4.436-2.026,4.121l1.96-4.153-2.729,3.693l2.669-3.736-3.344,3.146l3.293-3.199-3.852,2.498l3.812-2.56-4.237,1.771l4.208-1.838-4.485.986l4.469-1.058-4.589.169l4.585-.242-4.545-.653l4.555.58-4.355-1.454l4.378,1.384-4.026-2.208l4.061,2.143-3.567-2.891l3.612,2.834-2.993-3.482l3.049,3.433-2.324-3.96l2.387,3.923-1.579-4.312l1.648,4.286-.784-4.525.856,4.512.037-4.592Z"
                  transform="matrix(.8 0 0 0.8-308.398 97.198)"
                  opacity="0"
                  fill="#fff"
                />
                <path
                  id="ejzKihZF9pk17"
                  d="M508.008,112l.037,4.592.856-4.512-.784,4.525l1.648-4.286-1.579,4.312l2.386-3.923-2.323,3.96l3.049-3.433-2.994,3.482l3.613-2.834-3.567,2.891l4.061-2.143-4.026,2.208l4.378-1.384-4.355,1.454l4.555-.58-4.546.653l4.586.242-4.589-.169l4.468,1.058-4.484-.986l4.207,1.838-4.236-1.771l3.812,2.56-3.853-2.498l3.294,3.199-3.344-3.146l2.669,3.736-2.729-3.693l1.959,4.153-2.025-4.121l1.186,4.436-1.257-4.417.375,4.577-.448-4.57-.448,4.57.375-4.577-1.257,4.417l1.186-4.436-2.026,4.121l1.96-4.153-2.729,3.693l2.669-3.736-3.344,3.146l3.293-3.199-3.852,2.498l3.812-2.56-4.237,1.771l4.208-1.838-4.485.986l4.469-1.058-4.589.169l4.585-.242-4.545-.653l4.555.58-4.355-1.454l4.378,1.384-4.026-2.208l4.061,2.143-3.567-2.891l3.612,2.834-2.993-3.482l3.049,3.433-2.324-3.96l2.387,3.923-1.579-4.312l1.648,4.286-.784-4.525.856,4.512.037-4.592Z"
                  transform="matrix(.9 0 0 0.9-302.199-15.301)"
                  opacity="0"
                  fill="#fff"
                />
                <path
                  id="ejzKihZF9pk18"
                  d="M195.008,84l.037,4.5917.856-4.5113-.784,4.5244l1.648-4.286-1.579,4.3118l2.386-3.9228-2.323,3.9605l3.049-3.4337-2.994,3.4821l3.613-2.8341-3.567,2.8915l4.061-2.1434-4.026,2.2082l4.378-1.384-4.355,1.4539l4.555-.58-4.546.6528l4.586.2427-4.589-.1693l4.468,1.0576-4.484-.9859l4.207,1.8384-4.236-1.7708l3.812,2.5602-3.853-2.4989l3.294,3.1997-3.344-3.1465l2.669,3.7363-2.729-3.6931l1.959,4.1528-2.025-4.1209l1.186,4.4359-1.257-4.4164.375,4.5765-.448-4.5699-.448,4.5699.375-4.5765-1.257,4.4164l1.186-4.4359-2.026,4.1209l1.96-4.1528-2.729,3.6931l2.669-3.7363-3.344,3.1465l3.293-3.1997-3.852,2.4989l3.812-2.5602-4.237,1.7708l4.208-1.8384-4.485.9859l4.469-1.0576-4.589.1693l4.585-.2427-4.545-.6528l4.555.58-4.355-1.4539l4.378,1.384-4.026-2.2082l4.061,2.1434-3.567-2.8915l3.612,2.8341-2.993-3.4821l3.049,3.4337-2.324-3.9605l2.387,3.9228-1.579-4.3118l1.648,4.286-.784-4.5244.856,4.5113L195.008,84Z"
                  transform="matrix(1.1 0 0 1.1 340.499 227.101)"
                  opacity="0"
                  fill="#fff"
                />
                <path
                  id="ejzKihZF9pk19"
                  d="M538.008,211l.037,4.592.856-4.512-.784,4.525l1.648-4.286-1.579,4.312l2.386-3.923-2.323,3.96l3.049-3.433-2.994,3.482l3.613-2.834-3.567,2.891l4.061-2.143-4.026,2.208l4.378-1.384-4.355,1.454l4.555-.58-4.546.653l4.586.242-4.589-.169l4.468,1.058-4.484-.986l4.207,1.838-4.236-1.771l3.812,2.56-3.853-2.498l3.294,3.199-3.344-3.146l2.669,3.736-2.729-3.693l1.959,4.153-2.025-4.121l1.186,4.436-1.257-4.417.375,4.577-.448-4.57-.448,4.57.375-4.577-1.257,4.417l1.186-4.436-2.026,4.121l1.96-4.153-2.729,3.693l2.669-3.736-3.344,3.146l3.293-3.199-3.852,2.498l3.812-2.56-4.237,1.771l4.208-1.838-4.485.986l4.469-1.058-4.589.169l4.585-.242-4.545-.653l4.555.58-4.355-1.454l4.378,1.384-4.026-2.208l4.061,2.143-3.567-2.891l3.612,2.834-2.993-3.482l3.049,3.433-2.324-3.96l2.387,3.923-1.579-4.312l1.648,4.286-.784-4.525.856,4.512.037-4.592Z"
                  transform="matrix(.9 0 0 0.9 115.801 52.599)"
                  opacity="0"
                  fill="#fff"
                />
                <path
                  id="ejzKihZF9pk20"
                  d="M538.008,211l.037,4.592.856-4.512-.784,4.525l1.648-4.286-1.579,4.312l2.386-3.923-2.323,3.96l3.049-3.433-2.994,3.482l3.613-2.834-3.567,2.891l4.061-2.143-4.026,2.208l4.378-1.384-4.355,1.454l4.555-.58-4.546.653l4.586.242-4.589-.169l4.468,1.058-4.484-.986l4.207,1.838-4.236-1.771l3.812,2.56-3.853-2.498l3.294,3.199-3.344-3.146l2.669,3.736-2.729-3.693l1.959,4.153-2.025-4.121l1.186,4.436-1.257-4.417.375,4.577-.448-4.57-.448,4.57.375-4.577-1.257,4.417l1.186-4.436-2.026,4.121l1.96-4.153-2.729,3.693l2.669-3.736-3.344,3.146l3.293-3.199-3.852,2.498l3.812-2.56-4.237,1.771l4.208-1.838-4.485.986l4.469-1.058-4.589.169l4.585-.242-4.545-.653l4.555.58-4.355-1.454l4.378,1.384-4.026-2.208l4.061,2.143-3.567-2.891l3.612,2.834-2.993-3.482l3.049,3.433-2.324-3.96l2.387,3.923-1.579-4.312l1.648,4.286-.784-4.525.856,4.512.037-4.592Z"
                  transform="matrix(.7 0 0 0.7 144.402 340.797)"
                  opacity="0"
                  fill="#fff"
                />
                <path
                  id="ejzKihZF9pk21"
                  d="M508.008,112l.037,4.592.856-4.512-.784,4.525l1.648-4.286-1.579,4.312l2.386-3.923-2.323,3.96l3.049-3.433-2.994,3.482l3.613-2.834-3.567,2.891l4.061-2.143-4.026,2.208l4.378-1.384-4.355,1.454l4.555-.58-4.546.653l4.586.242-4.589-.169l4.468,1.058-4.484-.986l4.207,1.838-4.236-1.771l3.812,2.56-3.853-2.498l3.294,3.199-3.344-3.146l2.669,3.736-2.729-3.693l1.959,4.153-2.025-4.121l1.186,4.436-1.257-4.417.375,4.577-.448-4.57-.448,4.57.375-4.577-1.257,4.417l1.186-4.436-2.026,4.121l1.96-4.153-2.729,3.693l2.669-3.736-3.344,3.146l3.293-3.199-3.852,2.498l3.812-2.56-4.237,1.771l4.208-1.838-4.485.986l4.469-1.058-4.589.169l4.585-.242-4.545-.653l4.555.58-4.355-1.454l4.378,1.384-4.026-2.208l4.061,2.143-3.567-2.891l3.612,2.834-2.993-3.482l3.049,3.433-2.324-3.96l2.387,3.923-1.579-4.312l1.648,4.286-.784-4.525.856,4.512.037-4.592Z"
                  transform="matrix(1.6 0 0 1.6-321.805 205.806)"
                  opacity="0"
                  fill="#fff"
                />
                <path
                  id="ejzKihZF9pk22"
                  d="M195.008,84l.037,4.5917.856-4.5113-.784,4.5244l1.648-4.286-1.579,4.3118l2.386-3.9228-2.323,3.9605l3.049-3.4337-2.994,3.4821l3.613-2.8341-3.567,2.8915l4.061-2.1434-4.026,2.2082l4.378-1.384-4.355,1.4539l4.555-.58-4.546.6528l4.586.2427-4.589-.1693l4.468,1.0576-4.484-.9859l4.207,1.8384-4.236-1.7708l3.812,2.5602-3.853-2.4989l3.294,3.1997-3.344-3.1465l2.669,3.7363-2.729-3.6931l1.959,4.1528-2.025-4.1209l1.186,4.4359-1.257-4.4164.375,4.5765-.448-4.5699-.448,4.5699.375-4.5765-1.257,4.4164l1.186-4.4359-2.026,4.1209l1.96-4.1528-2.729,3.6931l2.669-3.7363-3.344,3.1465l3.293-3.1997-3.852,2.4989l3.812-2.5602-4.237,1.7708l4.208-1.8384-4.485.9859l4.469-1.0576-4.589.1693l4.585-.2427-4.545-.6528l4.555.58-4.355-1.4539l4.378,1.384-4.026-2.2082l4.061,2.1434-3.567-2.8915l3.612,2.8341-2.993-3.4821l3.049,3.4337-2.324-3.9605l2.387,3.9228-1.579-4.3118l1.648,4.286-.784-4.5244.856,4.5113L195.008,84Z"
                  transform="matrix(1.5 0 0 1.5-114.504 231.505)"
                  opacity="0"
                  fill="#fff"
                />
                <path
                  id="ejzKihZF9pk23"
                  d="M556.008,432l.037,4.592.856-4.512-.784,4.525l1.648-4.286-1.579,4.312l2.386-3.923-2.323,3.96l3.049-3.433-2.994,3.482l3.613-2.834-3.567,2.891l4.061-2.143-4.026,2.208l4.378-1.384-4.355,1.454l4.555-.58-4.546.653l4.586.242-4.589-.169l4.468,1.058-4.484-.986l4.207,1.838-4.236-1.771l3.812,2.56-3.853-2.498l3.294,3.199-3.344-3.146l2.669,3.736-2.729-3.693l1.959,4.153-2.025-4.121l1.186,4.436-1.257-4.417.375,4.577-.448-4.57-.448,4.57.375-4.577-1.257,4.417l1.186-4.436-2.026,4.121l1.96-4.153-2.729,3.693l2.669-3.736-3.344,3.146l3.293-3.199-3.852,2.498l3.812-2.56-4.237,1.771l4.208-1.838-4.485.986l4.469-1.058-4.589.169l4.585-.242-4.545-.653l4.555.58-4.355-1.454l4.378,1.384-4.026-2.208l4.061,2.143-3.567-2.891l3.612,2.834-2.993-3.482l3.049,3.433-2.324-3.96l2.387,3.923-1.579-4.312l1.648,4.286-.784-4.525.856,4.512.037-4.592Z"
                  transform="translate(-180-21)"
                  opacity="0"
                  fill="#fff"
                />
                <path
                  id="ejzKihZF9pk24"
                  d="M212.008,131l.037,4.592.856-4.512-.784,4.525l1.648-4.286-1.579,4.312l2.386-3.923-2.323,3.96l3.049-3.433-2.994,3.482l3.613-2.834-3.567,2.891l4.061-2.143-4.026,2.208l4.378-1.384-4.355,1.454l4.555-.58-4.546.653l4.586.242-4.589-.169l4.468,1.058-4.484-.986l4.207,1.838-4.236-1.771l3.812,2.56-3.853-2.498l3.294,3.199-3.344-3.146l2.669,3.736-2.729-3.693l1.959,4.153-2.025-4.121l1.186,4.436-1.257-4.417.375,4.577-.448-4.57-.448,4.57.375-4.577-1.257,4.417l1.186-4.436-2.026,4.121l1.96-4.153-2.729,3.693l2.669-3.736-3.344,3.146l3.293-3.199-3.852,2.498l3.812-2.56-4.237,1.771l4.208-1.838-4.485.986l4.469-1.058-4.589.169l4.585-.242-4.545-.653l4.555.58-4.355-1.454l4.378,1.384-4.026-2.208l4.061,2.143-3.567-2.891l3.612,2.834-2.993-3.482l3.049,3.433-2.324-3.96l2.387,3.923-1.579-4.312l1.648,4.286-.784-4.525.856,4.512.037-4.592Z"
                  transform="matrix(.8 0 0 0.8 25.4016 303.198)"
                  opacity="0"
                  fill="#fff"
                />
                <path
                  id="ejzKihZF9pk25"
                  d="M212.008,131l.037,4.592.856-4.512-.784,4.525l1.648-4.286-1.579,4.312l2.386-3.923-2.323,3.96l3.049-3.433-2.994,3.482l3.613-2.834-3.567,2.891l4.061-2.143-4.026,2.208l4.378-1.384-4.355,1.454l4.555-.58-4.546.653l4.586.242-4.589-.169l4.468,1.058-4.484-.986l4.207,1.838-4.236-1.771l3.812,2.56-3.853-2.498l3.294,3.199-3.344-3.146l2.669,3.736-2.729-3.693l1.959,4.153-2.025-4.121l1.186,4.436-1.257-4.417.375,4.577-.448-4.57-.448,4.57.375-4.577-1.257,4.417l1.186-4.436-2.026,4.121l1.96-4.153-2.729,3.693l2.669-3.736-3.344,3.146l3.293-3.199-3.852,2.498l3.812-2.56-4.237,1.771l4.208-1.838-4.485.986l4.469-1.058-4.589.169l4.585-.242-4.545-.653l4.555.58-4.355-1.454l4.378,1.384-4.026-2.208l4.061,2.143-3.567-2.891l3.612,2.834-2.993-3.482l3.049,3.433-2.324-3.96l2.387,3.923-1.579-4.312l1.648,4.286-.784-4.525.856,4.512.037-4.592Z"
                  transform="matrix(1.2 0 0 1.2-42.4016-27.198)"
                  opacity="0"
                  fill="#fff"
                />
                <path
                  id="ejzKihZF9pk26"
                  d="M538.008,211l.037,4.592.856-4.512-.784,4.525l1.648-4.286-1.579,4.312l2.386-3.923-2.323,3.96l3.049-3.433-2.994,3.482l3.613-2.834-3.567,2.891l4.061-2.143-4.026,2.208l4.378-1.384-4.355,1.454l4.555-.58-4.546.653l4.586.242-4.589-.169l4.468,1.058-4.484-.986l4.207,1.838-4.236-1.771l3.812,2.56-3.853-2.498l3.294,3.199-3.344-3.146l2.669,3.736-2.729-3.693l1.959,4.153-2.025-4.121l1.186,4.436-1.257-4.417.375,4.577-.448-4.57-.448,4.57.375-4.577-1.257,4.417l1.186-4.436-2.026,4.121l1.96-4.153-2.729,3.693l2.669-3.736-3.344,3.146l3.293-3.199-3.852,2.498l3.812-2.56-4.237,1.771l4.208-1.838-4.485.986l4.469-1.058-4.589.169l4.585-.242-4.545-.653l4.555.58-4.355-1.454l4.378,1.384-4.026-2.208l4.061,2.143-3.567-2.891l3.612,2.834-2.993-3.482l3.049,3.433-2.324-3.96l2.387,3.923-1.579-4.312l1.648,4.286-.784-4.525.856,4.512.037-4.592Z"
                  transform="matrix(2 0 0 2-538.008-215.99)"
                  opacity="0"
                  fill="#fff"
                />
                <path
                  id="ejzKihZF9pk27"
                  d="M508.008,112l.037,4.592.856-4.512-.784,4.525l1.648-4.286-1.579,4.312l2.386-3.923-2.323,3.96l3.049-3.433-2.994,3.482l3.613-2.834-3.567,2.891l4.061-2.143-4.026,2.208l4.378-1.384-4.355,1.454l4.555-.58-4.546.653l4.586.242-4.589-.169l4.468,1.058-4.484-.986l4.207,1.838-4.236-1.771l3.812,2.56-3.853-2.498l3.294,3.199-3.344-3.146l2.669,3.736-2.729-3.693l1.959,4.153-2.025-4.121l1.186,4.436-1.257-4.417.375,4.577-.448-4.57-.448,4.57.375-4.577-1.257,4.417l1.186-4.436-2.026,4.121l1.96-4.153-2.729,3.693l2.669-3.736-3.344,3.146l3.293-3.199-3.852,2.498l3.812-2.56-4.237,1.771l4.208-1.838-4.485.986l4.469-1.058-4.589.169l4.585-.242-4.545-.653l4.555.58-4.355-1.454l4.378,1.384-4.026-2.208l4.061,2.143-3.567-2.891l3.612,2.834-2.993-3.482l3.049,3.433-2.324-3.96l2.387,3.923-1.579-4.312l1.648,4.286-.784-4.525.856,4.512.037-4.592Z"
                  opacity="0"
                  fill="#fff"
                />
                <path
                  id="ejzKihZF9pk28"
                  d="M195.008,84l.037,4.5917.856-4.5113-.784,4.5244l1.648-4.286-1.579,4.3118l2.386-3.9228-2.323,3.9605l3.049-3.4337-2.994,3.4821l3.613-2.8341-3.567,2.8915l4.061-2.1434-4.026,2.2082l4.378-1.384-4.355,1.4539l4.555-.58-4.546.6528l4.586.2427-4.589-.1693l4.468,1.0576-4.484-.9859l4.207,1.8384-4.236-1.7708l3.812,2.5602-3.853-2.4989l3.294,3.1997-3.344-3.1465l2.669,3.7363-2.729-3.6931l1.959,4.1528-2.025-4.1209l1.186,4.4359-1.257-4.4164.375,4.5765-.448-4.5699-.448,4.5699.375-4.5765-1.257,4.4164l1.186-4.4359-2.026,4.1209l1.96-4.1528-2.729,3.6931l2.669-3.7363-3.344,3.1465l3.293-3.1997-3.852,2.4989l3.812-2.5602-4.237,1.7708l4.208-1.8384-4.485.9859l4.469-1.0576-4.589.1693l4.585-.2427-4.545-.6528l4.555.58-4.355-1.4539l4.378,1.384-4.026-2.2082l4.061,2.1434-3.567-2.8915l3.612,2.8341-2.993-3.4821l3.049,3.4337-2.324-3.9605l2.387,3.9228-1.579-4.3118l1.648,4.286-.784-4.5244.856,4.5113L195.008,84Z"
                  opacity="0"
                  fill="#fff"
                />
                <path
                  id="ejzKihZF9pk29"
                  d="M556.008,432l.037,4.592.856-4.512-.784,4.525l1.648-4.286-1.579,4.312l2.386-3.923-2.323,3.96l3.049-3.433-2.994,3.482l3.613-2.834-3.567,2.891l4.061-2.143-4.026,2.208l4.378-1.384-4.355,1.454l4.555-.58-4.546.653l4.586.242-4.589-.169l4.468,1.058-4.484-.986l4.207,1.838-4.236-1.771l3.812,2.56-3.853-2.498l3.294,3.199-3.344-3.146l2.669,3.736-2.729-3.693l1.959,4.153-2.025-4.121l1.186,4.436-1.257-4.417.375,4.577-.448-4.57-.448,4.57.375-4.577-1.257,4.417l1.186-4.436-2.026,4.121l1.96-4.153-2.729,3.693l2.669-3.736-3.344,3.146l3.293-3.199-3.852,2.498l3.812-2.56-4.237,1.771l4.208-1.838-4.485.986l4.469-1.058-4.589.169l4.585-.242-4.545-.653l4.555.58-4.355-1.454l4.378,1.384-4.026-2.208l4.061,2.143-3.567-2.891l3.612,2.834-2.993-3.482l3.049,3.433-2.324-3.96l2.387,3.923-1.579-4.312l1.648,4.286-.784-4.525.856,4.512.037-4.592Z"
                  opacity="0"
                  fill="#fff"
                />
              </g>
              <g
                id="ejzKihZF9pk30_ts"
                transform="translate(369.999497,456.085541)"
                onClick={props.properties?.handleClose}
              >
                <g transform="translate(-369.999496,-456.085541)">
                  <rect
                    width="287.179"
                    height="59.8291"
                    rx="4.78632"
                    ry="4.78632"
                    transform="translate(226.41 426.171)"
                    fill="#ed003f"
                  />
                  <g id="ejzKihZF9pk32_ts" transform="translate(369.999496,456.085541)">
                    <rect
                      id="ejzKihZF9pk32"
                      width="287.179"
                      height="59.8291"
                      rx="4.78632"
                      ry="4.78632"
                      transform="translate(-143.589496,-29.914541)"
                      fill="#ed003f"
                    />
                  </g>
                  <text dx="0" dy="0" fontSize="16" fontWeight="700" fill="#fff">
                    {t('CLAIM PRIZE')}
                  </text>
                </g>
              </g>
              <g id="ejzKihZF9pk35_ts" transform="translate(370,246)">
                <g transform="translate(-370,-246)" filter="url(#ejzKihZF9pk35-filter)">
                  <rect
                    width="360"
                    height="148"
                    rx="16"
                    ry="16"
                    transform="translate(190 246)"
                    fill="url(#ejzKihZF9pk36-fill)"
                  />
                  <rect
                    width="342"
                    height="130"
                    rx="13"
                    ry="13"
                    transform="translate(199 255)"
                    fill="none"
                    stroke="url(#ejzKihZF9pk37-stroke)"
                    strokeWidth="2"
                  />
                  <text
                    dx="0"
                    dy="0"
                    fontSize="60"
                    fontWeight="400"
                    fontStyle="italic"
                    letterSpacing="1.2"
                    fill="#fff"
                    fillOpacity="0.9"
                    stroke="#fff"
                    strokeWidth="1.5"
                  >
                    <tspan
                      x="284.282"
                      y="340.4"
                      fontSize="60"
                      fontWeight="400"
                      fontStyle="italic"
                      letterSpacing="1.2"
                      fill="#fff"
                      fillOpacity="0.9"
                      stroke="#fff"
                      strokeWidth="1.5"
                      style={{ textTransform: 'lowercase' }}
                    >
                      {countUp}&nbsp;{props?.properties?.currency ?? 'lei'}
                    </tspan>
                  </text>
                </g>
              </g>
              <g id="ejzKihZF9pk42_ts" transform="translate(369.471368,323.820359)">
                <image
                  id="ejzKihZF9pk42"
                  style={{ mixBlendMode: 'overlay' }}
                  width="184"
                  height="183"
                  xlinkHref={StarGlow}
                  preserveAspectRatio="xMidYMid meet"
                  transform="translate(-92,-91.5)"
                  opacity="0.05"
                />
              </g>
              <g id="ejzKihZF9pk43_ts" transform="translate(367.333316,316.000138)">
                <image
                  id="ejzKihZF9pk43"
                  style={{ mixBlendMode: 'screen' }}
                  width="118"
                  height="126"
                  xlinkHref={WhiteGlow}
                  preserveAspectRatio="xMidYMid meet"
                  transform="translate(-59,-63)"
                  opacity="0.27"
                />
              </g>
              <g id="ejzKihZF9pk44_ts" transform="translate(367.333316,228.67975)">
                <image
                  id="ejzKihZF9pk44"
                  style={{ mixBlendMode: 'overlay' }}
                  width="196"
                  height="196"
                  xlinkHref={WhiteGlowCircle}
                  preserveAspectRatio="xMidYMid meet"
                  transform="translate(-98,-98)"
                  opacity="0"
                />
              </g>
              <g id="ejzKihZF9pk45_to" transform="translate(363.238007,216.489418)">
                <g id="ejzKihZF9pk45_ts">
                  <g transform="translate(-363.238007,-216.489418)" filter="url(#ejzKihZF9pk45-filter)">
                    <path
                      d="M190,203.457C190,197.13,195.13,192,201.457,192h337.086C544.87,192,550,197.13,550,203.457v47.086C550,256.87,544.87,262,538.543,262h-337.086C195.13,262,190,256.87,190,250.543v-47.086Z"
                      fill="url(#ejzKihZF9pk46-fill)"
                    />
                    <g mask="url(#ejzKihZF9pk49)">
                      <path
                        d="M190,192h360-360Zm360,58.543c0,9.089-7.368,16.457-16.457,16.457h-327.086C197.368,267,190,259.632,190,250.543c0,3.566,5.13,6.457,11.457,6.457h337.086c6.327,0,11.457-2.891,11.457-6.457ZM190,262v-70v70Zm360-70v70-70Z"
                        fill="#240048"
                      />
                      <mask
                        id="ejzKihZF9pk49"
                        // eslint-disable-next-line
                        mask-type="luminance"
                        x="-150%"
                        y="-150%"
                        height="400%"
                        width="400%"
                      >
                        <path
                          d="M190,203.457C190,197.13,195.13,192,201.457,192h337.086C544.87,192,550,197.13,550,203.457v47.086C550,256.87,544.87,262,538.543,262h-337.086C195.13,262,190,256.87,190,250.543v-47.086Z"
                          fill="#fff"
                        />
                      </mask>
                    </g>
                    <g filter="url(#ejzKihZF9pk51-filter)">
                      <text dx="0" dy="0" fontFamily='&quotPaytone One"' fontSize="40" fontWeight="400" fill="#ffbf1a">
                        {t('YOU WON')}
                      </text>
                    </g>
                  </g>
                </g>
              </g>
              <g
                id="ejzKihZF9pk54_to"
                style={{
                  offsetPath:
                    "path('M384.156274,234.375946C457.013915,250.05437,545.168422,210.834183,560.003261,155.365413C527.202215,177.319746,524.578037,172.659385,515.95636,168.580106C515.853639,180.372031,531.395537,189.69381,531.412506,189.693832Q531.433271,189.69381,531.412506,189.693832Q531.395538,189.69468,531.412506,189.693832Q531.395538,189.69468,531.412506,189.693832Q531.32007,189.69468,531.412506,189.693832Q531.357805,189.69468,531.412506,189.693832')",
                  offsetRotate: '0deg',
                }}
              >
                <g id="ejzKihZF9pk54_tr" transform="rotate(76.516229)">
                  <g id="ejzKihZF9pk54_ts">
                    <g transform="translate(-531.412506,-189.693832)">
                      <g filter="url(#ejzKihZF9pk55-filter)">
                        <path
                          d="M548.614,186.187c-6.274,14.818-18.931,24.27-24.918,27.387-.242.126-.518.149-.781.074-.657-.185-.984-.919-.685-1.531l9.985-20.415c.059-.121.105-.247.139-.377l5.923-22.907c.187-.721.784-1.262,1.52-1.378.854-.134,1.688.334,2.017,1.132l6.802,16.51c.198.482.201,1.025-.002,1.505Z"
                          fill="url(#ejzKihZF9pk56-fill)"
                        />
                      </g>
                      <g filter="url(#ejzKihZF9pk57-filter)">
                        <path
                          d="M546.524,184.182l-7.748-18.33c-.096-.228-.449-.206-.53.028-.729,2.114-2.976,6.931-7.899,14.197-5.519,8.143-12.926,13.152-16.145,14.753-.118.058-.171.193-.127.316l6.533,18.383c.047.133.191.203.321.147c1.562-.67,6.083-3.513,13.649-10.498c7.93-7.322,11.263-15.563,11.961-18.849.011-.05.005-.1-.015-.147Z"
                          fill="url(#ejzKihZF9pk58-fill)"
                        />
                      </g>
                      <g filter="url(#ejzKihZF9pk59-filter)">
                        <path
                          d="M537.768,173.507c-1.338.882-2-.119-2.164-.73-1.025,1.475-4.116,5.685-8.273,10.727s-7.684,7.623-8.928,8.283c1.504,1.694.019,4.143-.912,5.156l3.296,8.615c1.958-.524,3.035.717,3.328,1.402.557-.367,3.078-2.461,8.704-7.895c5.626-5.435,7.899-10.082,8.333-11.726-1.635-2.183-.74-4.387-.088-5.217l-3.296-8.615Z"
                          fill="#d8f3db"
                        />
                      </g>
                      <ellipse
                        rx="5.68503"
                        ry="3.34816"
                        transform="matrix(-.038902 0.998692 1.00017 0.027903 529.15857 191.015015)"
                        fill="#26782e"
                      />
                      <g filter="url(#ejzKihZF9pk62-filter)">
                        <ellipse
                          rx="5.32722"
                          ry="2.90513"
                          transform="matrix(-.038902 0.998692 1.00017 0.027903 529.600384 191.028312)"
                          fill="url(#ejzKihZF9pk63-fill)"
                        />
                      </g>
                    </g>
                  </g>
                </g>
              </g>
              <g
                id="ejzKihZF9pk64_to"
                style={{
                  offsetPath:
                    "path('M351.768304,233.064704C304.408018,265.703373,219.907355,232.902769,182.609001,168.907842C182.098295,192.146079,189.633225,222.054614,212.008,214.395774C217.227695,233.249705,197.585616,249.264576,197.70871,249.240402Q197.8125,249.264576,197.70871,249.240402Q197.8125,249.354188,197.70871,249.240402Q197.585616,249.354188,197.70871,249.240402Q197.585616,249.354188,197.70871,249.240402Q197.585616,249.354188,197.70871,249.240402')",
                  offsetRotate: '0deg',
                }}
              >
                <g id="ejzKihZF9pk64_tr" transform="rotate(-62.373337)">
                  <g id="ejzKihZF9pk64_ts">
                    <g transform="translate(-197.70871,-249.240402)">
                      <g filter="url(#ejzKihZF9pk65-filter)">
                        <path
                          d="M196.526,266.879c-12.696-9.887-18.56-24.555-20.025-31.144-.06-.266-.01-.539.129-.774.349-.586,1.143-.713,1.657-.266l17.145,14.917c.101.088.211.166.328.232l20.601,11.636c.648.366,1.017,1.083.939,1.824-.09.86-.758,1.545-1.615,1.657l-17.706,2.309c-.516.067-1.041-.07-1.453-.391Z"
                          fill="url(#ejzKihZF9pk66-fill)"
                        />
                      </g>
                      <g filter="url(#ejzKihZF9pk67-filter)">
                        <path
                          d="M199.003,265.375l19.708-2.753c.245-.034.315-.381.11-.519-1.853-1.25-5.928-4.664-11.676-11.297-6.442-7.434-9.369-15.883-10.085-19.406-.026-.129-.142-.215-.273-.205l-19.446,1.566c-.14.012-.245.132-.225.272.244,1.682,1.824,6.784,6.619,15.897c5.026,9.551,12.127,14.899,15.121,16.422.046.023.096.03.147.023Z"
                          fill="url(#ejzKihZF9pk68-fill)"
                        />
                      </g>
                      <g filter="url(#ejzKihZF9pk69-filter)">
                        <path
                          d="M211.577,259.672c-.507-1.52.632-1.901,1.265-1.902-1.161-1.371-4.431-5.444-8.229-10.762-3.797-5.317-5.38-9.391-5.697-10.764-2.024,1.016-4.007-1.051-4.746-2.212l-9.174.96c.001,2.027-1.475,2.747-2.213,2.853.211.633,1.583,3.61,5.381,10.448s7.7,10.234,9.177,11.077c2.53-1.016,4.429.418,5.062,1.262l9.174-.96Z"
                          fill="#d8f3db"
                        />
                      </g>
                      <ellipse
                        rx="5.68503"
                        ry="3.34816"
                        transform="matrix(-.954791-.295421-.285175 0.959057 196.883171 246.834597)"
                        fill="#26782e"
                      />
                      <g filter="url(#ejzKihZF9pk72-filter)">
                        <ellipse
                          rx="5.32722"
                          ry="2.90513"
                          transform="matrix(-.954791-.295421-.285175 0.959057 196.757146 247.257411)"
                          fill="url(#ejzKihZF9pk73-fill)"
                        />
                      </g>
                    </g>
                  </g>
                </g>
              </g>
              <g id="ejzKihZF9pk74_to" transform="translate(370.693504,281.025215)">
                <g id="ejzKihZF9pk74_ts" transform="scale(2.040922,2.040913)">
                  <g transform="translate(-214.693504,-86.025215)">
                    <g id="ejzKihZF9pk75_ts" transform="translate(214.693504,78.980949)">
                      <g transform="translate(-214.693504,-78.980949)" filter="url(#ejzKihZF9pk75-filter)">
                        <path
                          d="M154.542,71.3658c0-4.7563,2.939-9.0179,7.384-10.7089L210.62,42.1354c2.624-.998,5.523-.998,8.147,0l48.694,18.5215c4.445,1.691,7.384,5.9527,7.384,10.7089v10.6003c0,4.4217-2.544,8.4487-6.538,10.3474L219.613,115.465c-3.113,1.48-6.726,1.48-9.839,0L161.08,92.3135c-3.993-1.8987-6.538-5.9257-6.538-10.3474v-10.6003Z"
                          fill="url(#ejzKihZF9pk76-fill)"
                        />
                      </g>
                    </g>
                    <g id="ejzKihZF9pk77_ts" transform="translate(215.852394,56.452301)">
                      <g transform="translate(-215.852394,-56.452301)" filter="url(#ejzKihZF9pk77-filter)">
                        <path
                          d="M168.764,48.8676l.811-4.7904h20.627l-.811,4.7904h-7.4L178.75,68.4818h-5.815l3.241-19.6142h-7.412ZM199.66,60.5813l1.74-10.4029h5.815l-3.05,18.3034h-5.553l.56-3.4081h-.191c-.564,1.1202-1.394,2.0099-2.49,2.6693-1.097.6514-2.332.9771-3.706.9771-1.248,0-2.296-.286-3.146-.858-.85-.5719-1.454-1.3703-1.812-2.3951-.349-1.0328-.413-2.2403-.19-3.6226l1.954-11.666h5.827l-1.752,10.522c-.151.9931-.019,1.7756.394,2.3476.421.5719,1.08.8579,1.978.8579.58,0,1.124-.1271,1.632-.3813.509-.2621.938-.6395,1.287-1.132.358-.5005.592-1.1043.703-1.8113Zm8.214,7.9005l3.05-18.3034h5.66l-.56,3.3365h.191c.532-1.2075,1.227-2.1052,2.085-2.693.858-.5959,1.788-.8938,2.789-.8938.27,0,.54.0199.81.0596.278.0318.54.0795.787.143l-.858,5.0763c-.271-.1032-.624-.1787-1.061-.2264-.437-.0556-.834-.0834-1.192-.0834-.691,0-1.334.1549-1.93.4648-.588.3018-1.088.7268-1.502,1.275-.405.5402-.671,1.1757-.798,1.9066l-1.644,9.9382h-5.827Zm14.123,0l4.052-24.4046h5.827l-1.513,9.2352h.119c.317-.5561.739-1.0924,1.263-1.6087.532-.5164,1.172-.9375,1.918-1.2632.747-.3336,1.601-.5005,2.562-.5005c1.271,0,2.403.3337,3.397,1.001.993.6673,1.715,1.6961,2.168,3.0863.453,1.3903.501,3.1618.143,5.3147-.341,2.0734-.957,3.8092-1.847,5.2074s-1.95,2.4468-3.181,3.1459c-1.224.6991-2.511,1.0486-3.861,1.0486-.922,0-1.696-.1509-2.324-.4528-.62-.3019-1.116-.6991-1.489-1.1916-.366-.4925-.62-1.0208-.763-1.5849h-.227l-.476,2.9672h-5.768Zm7.234-9.1517c-.159.9851-.167,1.843-.024,2.5739.151.7309.441,1.2989.87,1.704.437.3972,1.012.5958,1.727.5958s1.355-.1986,1.919-.5958c.564-.4051,1.037-.9731,1.418-1.704s.651-1.5888.81-2.5739.167-1.8391.024-2.562c-.135-.7229-.417-1.283-.846-1.6802s-1.005-.5958-1.728-.5958c-.715,0-1.358.1946-1.93.5839-.564.3892-1.041.9453-1.43,1.6682-.381.723-.652,1.5849-.81,2.5859Zm22.763,9.4973c-1.931,0-3.524-.3973-4.779-1.1917-1.247-.7944-2.125-1.9026-2.633-3.3246-.509-1.422-.608-3.0744-.298-4.9572.31-1.8748.949-3.5193,1.918-4.9333s2.209-2.5144,3.718-3.3008c1.51-.7865,3.222-1.1798,5.136-1.1798c1.923,0,3.507.3973,4.755,1.1917c1.255.7944,2.137,1.9026,2.645,3.3246.516,1.422.62,3.0784.31,4.9691-.31,1.8748-.953,3.5193-1.931,4.9333-.977,1.4062-2.216,2.5025-3.717,3.2889-1.502.7865-3.21,1.1798-5.124,1.1798Zm.655-4.3972c.715,0,1.354-.2144,1.919-.6434.571-.429,1.052-1.0288,1.441-1.7994.39-.7706.668-1.6643.835-2.6812.166-1.0009.182-1.8788.047-2.6335-.127-.7626-.397-1.3544-.81-1.7755-.413-.429-.973-.6435-1.68-.6435-.715,0-1.359.2185-1.931.6554-.572.429-1.052,1.0288-1.442,1.7994-.381.7626-.655,1.6564-.822,2.6812-.167,1.0009-.187,1.8788-.059,2.6335.127.7547.401,1.3465.822,1.7755.421.421.981.6315,1.68.6315Z"
                          fill="#f9c448"
                        />
                        <g mask="url(#ejzKihZF9pk81)">
                          <path
                            d="M168.764,48.8676l-1.181-.1999c-.059.3479.038.704.266.9734s.563.4249.915.4249v-1.1984Zm.811-4.7904v-1.1984c-.585,0-1.084.422-1.182.9986l1.182.1998Zm20.627,0l1.181.1999c.059-.3478-.038-.7039-.266-.9734s-.562-.4249-.915-.4249v1.1984Zm-.811,4.7904v1.1984c.585,0,1.085-.422,1.182-.9985l-1.182-.1999Zm-7.4,0v-1.1985c-.586,0-1.086.4245-1.182,1.0031l1.182.1954ZM178.75,68.4818v1.1984c.587,0,1.087-.4244,1.183-1.003l-1.183-.1954Zm-5.815,0l-1.182-.1954c-.058.3473.04.7022.268.9707s.562.4231.914.4231v-1.1984Zm3.241-19.6142l1.183.1954c.057-.3473-.041-.7023-.269-.9707s-.562-.4232-.914-.4232v1.1985Zm-6.23.1999l.81-4.7904-2.363-.3997-.81,4.7903l2.363.3998Zm-.371-3.7918h20.627v-2.3969h-20.627v2.3969Zm19.445-1.3983l-.81,4.7903l2.363.3998.81-4.7904-2.363-.3997Zm.371,3.7917h-7.4v2.3969h7.4v-2.3969Zm-8.582,1.0031l-3.241,19.6142l2.365.3908l3.241-19.6142-2.365-.3908ZM178.75,67.2833h-5.815v2.3969h5.815v-2.3969Zm-4.633,1.3939l3.242-19.6142-2.365-.3908-3.241,19.6142l2.364.3908Zm2.059-21.0081h-7.412v2.3969h7.412v-2.3969ZM199.66,60.5813l-1.182-.1977-.002.0115l1.184.1862Zm1.74-10.4029v-1.1985c-.586,0-1.085.4232-1.182,1.0008l1.182.1977Zm5.815,0l1.182.197c.058-.3475-.04-.7029-.267-.9717-.228-.2688-.563-.4238-.915-.4238v1.1985Zm-3.05,18.3034v1.1984c.585,0,1.085-.4235,1.182-1.0014l-1.182-.197Zm-5.553,0l-1.183-.1944c-.057.3472.041.7019.269.9701s.562.4227.914.4227v-1.1984Zm.56-3.4081l1.182.1944c.057-.3472-.041-.7019-.269-.9701-.227-.2681-.561-.4227-.913-.4227v1.1984Zm-.191,0v-1.1984c-.453,0-.867.2551-1.07.6594l1.07.539Zm-2.49,2.6693l.612,1.0303.005-.0033-.617-1.027Zm-6.852.1191l-.669.9943.669-.9943Zm-1.812-2.3951l-1.135.3842.004.0105l1.131-.3947Zm-.19-3.6226l-1.182-.198-.001.0076l1.183.1904Zm1.954-11.666v-1.1985c-.585,0-1.085.4231-1.182,1.0005l1.182.198Zm5.827,0l1.182.1968c.058-.3475-.04-.7028-.268-.9715-.227-.2688-.562-.4238-.914-.4238v1.1985Zm-1.752,10.522l-1.182-.1968c-.001.0056-.002.0112-.002.0168l1.184.18Zm.394,2.3476l-.972.7017.006.0087.966-.7104Zm3.61.4766l.536,1.0719c.005-.0022.009-.0044.013-.0067l-.549-1.0652Zm1.287-1.132l-.975-.6966-.002.003.977.6936Zm1.885-1.6136l1.74-10.4029-2.364-.3954-1.74,10.4029l2.364.3954Zm.558-9.4022h5.815v-2.3969h-5.815v2.3969Zm4.633-1.3954l-3.051,18.3034l2.365.394l3.05-18.3034-2.364-.394Zm-1.868,17.3019h-5.553v2.3969h5.553v-2.3969Zm-4.371,1.3928l.56-3.408-2.365-.3887-.56,3.408l2.365.3887Zm-.622-4.8008h-.191v2.3969h.191v-2.3969Zm-1.261.6594c-.46.9138-1.132,1.6361-2.038,2.1813l1.235,2.054c1.286-.7736,2.275-1.8308,2.943-3.1573l-2.14-1.078Zm-2.033,2.178c-.896.5328-1.917.809-3.093.809v2.3968c1.572,0,3.022-.3752,4.318-1.1453l-1.225-2.0605Zm-3.093.809c-1.062,0-1.864-.2415-2.477-.6539l-1.338,1.9886c1.087.7316,2.381,1.0621,3.815,1.0621v-2.3968Zm-2.477-.6539c-.622-.4184-1.071-.9999-1.349-1.7956l-2.263.7895c.437,1.2539,1.195,2.2692,2.274,2.9947l1.338-1.9886Zm-1.345-1.7851c-.272-.8029-.343-1.805-.143-3.0479l-2.366-.3808c-.245,1.5217-.189,2.9346.238,4.1972l2.271-.7685Zm-.144-3.0403l1.954-11.666-2.364-.396-1.954,11.666l2.364.396Zm.772-10.6656h5.827v-2.3969h-5.827v2.3969Zm4.645-1.3952l-1.752,10.522l2.365.3937L196.6,50.3752l-2.364-.3936Zm-1.754,10.5388c-.178,1.1712-.057,2.3116.606,3.2292l1.943-1.4033c-.163-.2263-.304-.651-.18-1.4658l-2.369-.3601Zm.612,3.238c.706.9587,1.777,1.346,2.944,1.346v-2.3969c-.628,0-.877-.1848-1.013-.37l-1.931,1.4209Zm2.944,1.346c.762,0,1.49-.1687,2.168-.5079l-1.072-2.1438c-.338.1693-.699.2548-1.096.2548v2.3969Zm2.181-.5146c.689-.3549,1.261-.8638,1.716-1.5036L197.98,61.699c-.245.3452-.53.591-.859.7604l1.098,2.1304Zm1.713-1.5007c.483-.675.777-1.4606.912-2.3216l-2.368-.3724c-.087.5531-.261.975-.494,1.3009l1.95,1.3931Zm7.942,5.3927l-1.183-.197c-.057.3474.04.7028.268.9716s.562.4238.915.4238v-1.1984Zm3.05-18.3034v-1.1985c-.586,0-1.086.4236-1.182,1.0015l1.182.197Zm5.66,0l1.182.1984c.059-.3477-.039-.7034-.267-.9725-.227-.2692-.562-.4244-.915-.4244v1.1985Zm-.56,3.3365l-1.182-.1984c-.058.3477.04.7034.267.9725.228.2692.563.4244.915.4244v-1.1985Zm.191,0v1.1985c.475,0,.905-.2805,1.097-.7151l-1.097-.4834Zm2.085-2.693l.678.9886.006-.0043-.684-.9843Zm3.599-.8342l-.174,1.1857c.012.0019.025.0035.038.005l.136-1.1907Zm.787.143l1.181.1997c.104-.6136-.279-1.2046-.882-1.3601l-.299,1.1604Zm-.858,5.0763l-.428,1.1194c.335.1283.71.0994,1.022-.0788.312-.1781.527-.4867.587-.8408l-1.181-.1998Zm-1.061-.2264l-.151,1.1889c.007.0009.014.0017.021.0025l.13-1.1914Zm-3.122.3814l.547,1.0661.006-.0029-.553-1.0632Zm-1.502,1.275l-.957-.7213-.001.0022.958.7191Zm-.798,1.9066l-1.181-.2053-.001.0097l1.182.1956Zm-1.644,9.9382v1.1984c.586,0,1.086-.4243,1.182-1.0028l-1.182-.1956Zm-4.645.197l3.05-18.3034-2.364-.394-3.051,18.3034l2.365.394Zm1.868-17.302h5.66v-2.3969h-5.66v2.3969Zm4.478-1.3968l-.56,3.3365l2.364.3968.56-3.3365-2.364-.3968Zm.622,4.7334h.191v-2.3969h-.191v2.3969Zm1.288-.7151c.466-1.0577,1.035-1.7559,1.666-2.1878l-1.355-1.9773c-1.086.7438-1.906,1.841-2.505,3.1984l2.194.9667Zm1.672-2.1921c.666-.4629,1.359-.6796,2.105-.6796v-2.3969c-1.257,0-2.423.379-3.472,1.1078l1.367,1.9687Zm2.105-.6796c.211,0,.423.0155.636.0468l.348-2.3714c-.327-.0481-.655-.0723-.984-.0723v2.3969Zm.674.0518c.228.0261.435.0643.623.1127l.599-2.3208c-.305-.0787-.622-.1358-.95-.1733l-.272,2.3814Zm-.259-1.2474l-.858,5.0763l2.363.3995.858-5.0764-2.363-.3994Zm.752,4.1566c-.402-.1536-.867-.2447-1.359-.2983l-.26,2.3827c.382.0417.624.1016.763.1544l.856-2.2388Zm-1.338-.2958c-.476-.0606-.925-.093-1.343-.093v2.3969c.297,0,.643.0232,1.041.0738l.302-2.3777Zm-1.343-.093c-.877,0-1.711.1986-2.483.5999l1.106,2.1265c.42-.2183.873-.3295,1.377-.3295v-2.3969Zm-2.478.5971c-.754.3876-1.393.9331-1.911,1.6198l1.915,1.4426c.308-.4096.671-.7142,1.091-.9303l-1.095-2.1321Zm-1.912,1.622c-.53.706-.864,1.5218-1.021,2.4204l2.362.4106c.098-.5632.295-1.0184.576-1.3928l-1.917-1.4382Zm-1.022,2.4301l-1.645,9.9381l2.365.3913l1.644-9.9382-2.364-.3912Zm-.462,8.9353h-5.827v2.3969h5.827v-2.3969Zm8.296,1.1985l-1.182-.1963c-.058.3474.04.7026.268.9712s.562.4235.914.4235v-1.1984Zm4.052-24.4046v-1.1984c-.586,0-1.086.424-1.182,1.0022l1.182.1962Zm5.827,0l1.183.1938c.056-.347-.042-.7016-.27-.9696-.227-.2681-.561-.4226-.913-.4226v1.1984Zm-1.513,9.2352l-1.183-.1938c-.057.347.041.7016.269.9696s.562.4226.914.4226v-1.1984Zm.119,0v1.1984c.43,0,.827-.2305,1.04-.6039l-1.04-.5945Zm1.263-1.6087l-.835-.8602-.006.0063.841.8539Zm1.918-1.2632l.48,1.0986.009-.0044-.489-1.0942Zm5.959.5005l.668-.9947-.668.9947Zm2.168,3.0863l-1.139.3712v0l1.139-.3712Zm.143,5.3147l-1.182-.1963v.0015l1.182.1948Zm-1.847,5.2074l-1.011-.6434v0l1.011.6434Zm-3.181,3.1459l-.592-1.0422-.003.0017.595,1.0405Zm-6.185.5958l-.525,1.0774.006.0026.519-1.08Zm-1.489-1.1916l-.963.7141.007.0099.956-.724Zm-.763-1.5849l1.162-.2945c-.135-.5316-.614-.9039-1.162-.9039v1.1984Zm-.227,0v-1.1984c-.588,0-1.089.4273-1.183,1.0083l1.183.1901Zm-.476,2.9672v1.1984c.588,0,1.09-.4273,1.183-1.0083l-1.183-.1901Zm1.466-9.1517l1.183.1908v-.0029l-1.183-.1879Zm-.024,2.5739l-1.177.2301.003.0123l1.174-.2424Zm.87,1.704l-.823.8713c.005.0052.011.0104.016.0155l.807-.8868Zm3.646,0l.69.9799.009-.0065-.699-.9734Zm1.418-1.704l-1.063-.5544l1.063.5544Zm.834-5.1359l-1.178.2201.002.0124l1.176-.2325Zm-.846-1.6802l.814-.8794-.814.8794Zm-3.658-.0119l-.675-.9908-.006.0044.681.9864Zm-1.43,1.6682l-1.055-.5682-.005.0091l1.06.5591ZM223.18,68.6781l4.051-24.4046-2.364-.3925-4.052,24.4045l2.365.3926Zm2.869-23.4024h5.827v-2.3969h-5.827v2.3969Zm4.644-1.3923l-1.513,9.2352l2.365.3876l1.514-9.2352-2.366-.3876Zm-.33,10.6274h.119v-2.3969h-.119v2.3969Zm1.159-.6039c.256-.448.606-.8987,1.064-1.3494l-1.682-1.7077c-.591.5821-1.083,1.2038-1.463,1.868l2.081,1.1891Zm1.057-1.3431c.42-.4068.936-.7509,1.564-1.0248l-.959-2.197c-.865.3776-1.628.8756-2.274,1.5015l1.669,1.7203Zm1.573-1.0291c.569-.2539,1.251-.3962,2.073-.3962v-2.3969c-1.1,0-2.125.1914-3.051.6047l.978,2.1884Zm2.073-.3962c1.049,0,1.945.2712,2.728.7972l1.337-1.9894c-1.203-.8086-2.571-1.2047-4.065-1.2047v2.3969Zm2.728.7972c.725.4871,1.309,1.2691,1.698,2.4628l2.279-.7423c-.517-1.5868-1.379-2.8623-2.64-3.7099l-1.337,1.9894Zm1.698,2.4628c.371,1.1392.44,2.6981.1,4.7472l2.365.3926c.374-2.2567.348-4.2409-.186-5.8821l-2.279.7423Zm.1,4.7487c-.322,1.955-.893,3.5283-1.676,4.7588l2.022,1.2868c.997-1.5659,1.658-3.4642,2.019-5.656l-2.365-.3896Zm-1.676,4.7588c-.802,1.2612-1.728,2.16-2.762,2.7471l1.183,2.0844c1.429-.811,2.624-2.0095,3.601-3.5447l-2.022-1.2868Zm-2.765,2.7488c-1.05.6003-2.134.8907-3.266.8907v2.3969c1.568,0,3.059-.4087,4.455-1.2066l-1.189-2.081Zm-3.266.8907c-.798,0-1.383-.1314-1.804-.3344l-1.039,2.16c.833.4008,1.797.5713,2.843.5713v-2.3969Zm-1.799-.3318c-.47-.2291-.812-.5121-1.059-.8382l-1.911,1.448c.5.659,1.151,1.1703,1.92,1.545l1.05-2.1548Zm-1.052-.8283c-.277-.3739-.461-.7608-.563-1.1653l-2.324.589c.184.7237.509,1.3933.962,2.0045l1.925-1.4282Zm-1.725-2.0692h-.227v2.3969h.227v-2.3969Zm-1.41,1.0083l-.476,2.9672l2.366.3802.477-2.9672-2.367-.3802Zm.707,1.9588h-5.768v2.3969h5.768v-2.3969Zm.282-8.1441c-.174,1.0802-.194,2.087-.016,2.9949l2.352-.4602c-.109-.5538-.113-1.263.031-2.153l-2.367-.3817Zm-.014,3.0072c.187.9076.571,1.7192,1.221,2.3329l1.645-1.7426c-.208-.1965-.404-.521-.519-1.0751l-2.347.4848Zm1.237,2.3484c.702.6382,1.587.9075,2.534.9075v-2.3969c-.483,0-.749-.1279-.921-.2841l-1.613,1.7735Zm2.534.9075c.953,0,1.835-.2696,2.609-.8144l-1.38-1.9597c-.354.2495-.751.3772-1.229.3772v2.3969Zm2.618-.8209c.742-.5332,1.33-1.2569,1.782-2.123l-2.126-1.1088c-.31.5956-.668,1.0079-1.054,1.2851l1.398,1.9467Zm1.782-2.123c.451-.8659.755-1.8513.93-2.9375l-2.366-.3817c-.142.8841-.379,1.6146-.69,2.2104l2.126,1.1088Zm.93-2.9375c.174-1.079.195-2.0831.017-2.9854l-2.352.4651c.108.5436.113,1.2475-.031,2.1386l2.366.3817Zm.019-2.9729c-.17-.91-.548-1.7266-1.21-2.3395l-1.628,1.7587c.196.1816.382.4851.482,1.021l2.356-.4402Zm-1.21-2.3395c-.7-.648-1.592-.9149-2.542-.9149v2.3969c.496,0,.756.1304.914.2767l1.628-1.7587Zm-2.542-.9149c-.947,0-1.827.2623-2.604.7916l1.348,1.9815c.366-.2492.773-.3762,1.256-.3762v-2.3969Zm-2.611.796c-.748.5164-1.343,1.2293-1.804,2.0865l2.11,1.1363c.317-.5886.676-.988,1.055-1.2501l-1.361-1.9727Zm-1.809,2.0955c-.455.8631-.759,1.8563-.934,2.9571l2.367.3758c.143-.9012.379-1.6319.687-2.2146l-2.12-1.1183Zm18.234,11.4506l-.644,1.0108.003.0019.641-1.0127Zm-2.633-3.3246l-1.129.4035v0l1.129-.4035Zm-.298-4.9572l-1.183-.1954v.0008l1.183.1946Zm1.918-4.9333l.989.6775-.989-.6775Zm3.718-3.3008l.554,1.0628-.554-1.0628Zm9.891.0119l-.644,1.0108.003.0018.641-1.0126Zm2.645,3.3246l-1.128.4035.002.0056l1.126-.4091Zm.31,4.9691l1.182.1954.001-.0016-1.183-.1938Zm-1.931,4.9333l.985.6839.001-.0026-.986-.6813Zm-3.717,3.2889l.556,1.0617-.556-1.0617Zm-2.55-3.8608l-.72-.9588-.006.0049.726.9539Zm1.441-1.7994l1.07.5404-1.07-.5404Zm.835-2.6812l-1.183-.197v.003l1.183.194Zm.047-2.6335l-1.182.1971c.001.0047.002.0094.002.0141l1.18-.2112Zm-.81-1.7755l-.863.8313.007.008.856-.8393Zm-3.611.0119l.719.9588.009-.0064-.728-.9524Zm-1.442,1.7994l-1.069-.5404-.003.0044l1.072.536Zm-.822,2.6812l1.182.197.001-.0045-1.183-.1925Zm-.059,2.6335l1.181-.1991-1.181.1991Zm.822,1.7755l-.856.8395.008.0079.848-.8474Zm1.025,3.8302c-1.771,0-3.124-.3642-4.138-1.0058l-1.282,2.0253c1.497.9471,3.329,1.3774,5.42,1.3774v-2.3969Zm-4.135-1.004c-1.018-.6481-1.728-1.5402-2.149-2.7173l-2.257.807c.596,1.6669,1.641,2.9913,3.118,3.9319l1.288-2.0216Zm-2.149-2.7173c-.424-1.1864-.529-2.6251-.244-4.3591l-2.365-.3892c-.334,2.0316-.24,3.8976.352,5.5553l2.257-.807Zm-.244-4.3583c.285-1.7208.865-3.1968,1.725-4.4512l-1.977-1.3551c-1.079,1.5737-1.777,3.3867-2.113,5.4155l2.365.3908Zm1.725-4.4512c.859-1.2532,1.949-2.2206,3.283-2.9155l-1.108-2.1257c-1.685.878-3.073,2.1112-4.152,3.6861l1.977,1.3551Zm3.283-2.9155c1.318-.6868,2.836-1.0441,4.582-1.0441v-2.3969c-2.083,0-3.989.4292-5.69,1.3153l1.108,2.1257Zm4.582-1.0441c1.762,0,3.106.3639,4.111,1.004l1.287-2.0216c-1.489-.9487-3.315-1.3793-5.398-1.3793v2.3969Zm4.114,1.0058c1.025.6489,1.738,1.5407,2.158,2.7155l2.257-.807c-.597-1.6692-1.648-2.9938-3.133-3.9338l-1.282,2.0253Zm2.16,2.7211c.43,1.1843.539,2.625.253,4.3662l2.366.3876c.334-2.0402.236-3.9123-.366-5.5719l-2.253.8181Zm.254,4.3646c-.285,1.719-.868,3.1936-1.734,4.4475l1.971,1.3625c1.088-1.5743,1.792-3.3885,2.127-5.4192l-2.364-.3908Zm-1.733,4.4449c-.868,1.2492-1.961,2.2152-3.289,2.9111l1.112,2.1233c1.674-.8771,3.059-2.1037,4.146-3.6667l-1.969-1.3677Zm-3.289,2.9111c-1.309.6855-2.822,1.0429-4.568,1.0429v2.3969c2.082,0,3.985-.4291,5.68-1.3165l-1.112-2.1233Zm-3.913-.9573c.974,0,1.869-.2989,2.644-.888l-1.451-1.9078c-.353.2688-.738.3989-1.193.3989v2.3969Zm2.638-.8832c.747-.5602,1.337-1.3163,1.792-2.2177L254.94,61.447c-.323.6398-.695,1.0833-1.092,1.381l1.439,1.9175Zm1.792-2.2177c.454-.8983.764-1.9128.947-3.0275l-2.365-.3881c-.151.919-.397,1.692-.721,2.3348l2.139,1.0808Zm.947-3.0245c.182-1.095.21-2.1163.045-3.0417l-2.36.4223c.105.584.102,1.3184-.05,2.2253l2.365.3941Zm.047-3.0276c-.155-.9305-.505-1.774-1.137-2.4178l-1.711,1.6786c.195.1983.385.5385.484,1.1333l2.364-.3941Zm-1.129-2.4098c-.681-.7071-1.576-1.0106-2.543-1.0106v2.3969c.447,0,.671.1255.817.2763l1.726-1.6626Zm-2.543-1.0106c-.98,0-1.879.3061-2.658.9015l1.455,1.9047c.364-.2785.753-.4093,1.203-.4093v-2.3969Zm-2.65.8951c-.747.5602-1.337,1.3163-1.792,2.2177l2.139,1.0808c.323-.6398.694-1.0833,1.091-1.381l-1.438-1.9175Zm-1.795,2.2221c-.445.8914-.751,1.9054-.933,3.0246l2.366.3851c.151-.9304.394-1.7039.711-2.3377l-2.144-1.072Zm-.932,3.0201c-.182,1.092-.214,2.1089-.059,3.0296l2.363-.3981c-.099-.5887-.091-1.3274.06-2.2374l-2.364-.3941Zm-.059,3.0296c.156.9304.514,1.7695,1.148,2.4159l1.711-1.6789c-.208-.2115-.398-.5561-.496-1.1351l-2.363.3981Zm1.156,2.4239c.685.6847,1.571.9826,2.528.9826v-2.3969c-.441,0-.675-.1232-.833-.2805l-1.695,1.6948Z"
                            fill="url(#ejzKihZF9pk80-fill)"
                          />
                          <mask
                            id="ejzKihZF9pk81"
                            // eslint-disable-next-line
                            mask-type="luminance"
                            x="-150%"
                            y="-150%"
                            height="400%"
                            width="400%"
                          >
                            <rect width="98" height="28" rx="0" ry="0" transform="translate(167 42.4818)" fill="#fff" />
                            <path
                              d="M168.764,48.8676l.811-4.7904h20.627l-.811,4.7904h-7.4L178.75,68.4818h-5.815l3.241-19.6142h-7.412ZM199.66,60.5813l1.74-10.4029h5.815l-3.05,18.3034h-5.553l.56-3.4081h-.191c-.564,1.1202-1.394,2.0099-2.49,2.6693-1.097.6514-2.332.9771-3.706.9771-1.248,0-2.296-.286-3.146-.858-.85-.5719-1.454-1.3703-1.812-2.3951-.349-1.0328-.413-2.2403-.19-3.6226l1.954-11.666h5.827l-1.752,10.522c-.151.9931-.019,1.7756.394,2.3476.421.5719,1.08.8579,1.978.8579.58,0,1.124-.1271,1.632-.3813.509-.2621.938-.6395,1.287-1.132.358-.5005.592-1.1043.703-1.8113Zm8.214,7.9005l3.05-18.3034h5.66l-.56,3.3365h.191c.532-1.2075,1.227-2.1052,2.085-2.693.858-.5959,1.788-.8938,2.789-.8938.27,0,.54.0199.81.0596.278.0318.54.0795.787.143l-.858,5.0763c-.271-.1032-.624-.1787-1.061-.2264-.437-.0556-.834-.0834-1.192-.0834-.691,0-1.334.1549-1.93.4648-.588.3018-1.088.7268-1.502,1.275-.405.5402-.671,1.1757-.798,1.9066l-1.644,9.9382h-5.827Zm14.123,0l4.052-24.4046h5.827l-1.513,9.2352h.119c.317-.5561.739-1.0924,1.263-1.6087.532-.5164,1.172-.9375,1.918-1.2632.747-.3336,1.601-.5005,2.562-.5005c1.271,0,2.403.3337,3.397,1.001.993.6673,1.715,1.6961,2.168,3.0863.453,1.3903.501,3.1618.143,5.3147-.341,2.0734-.957,3.8092-1.847,5.2074s-1.95,2.4468-3.181,3.1459c-1.224.6991-2.511,1.0486-3.861,1.0486-.922,0-1.696-.1509-2.324-.4528-.62-.3019-1.116-.6991-1.489-1.1916-.366-.4925-.62-1.0208-.763-1.5849h-.227l-.476,2.9672h-5.768Zm7.234-9.1517c-.159.9851-.167,1.843-.024,2.5739.151.7309.441,1.2989.87,1.704.437.3972,1.012.5958,1.727.5958s1.355-.1986,1.919-.5958c.564-.4051,1.037-.9731,1.418-1.704s.651-1.5888.81-2.5739.167-1.8391.024-2.562c-.135-.7229-.417-1.283-.846-1.6802s-1.005-.5958-1.728-.5958c-.715,0-1.358.1946-1.93.5839-.564.3892-1.041.9453-1.43,1.6682-.381.723-.652,1.5849-.81,2.5859Zm22.763,9.4973c-1.931,0-3.524-.3973-4.779-1.1917-1.247-.7944-2.125-1.9026-2.633-3.3246-.509-1.422-.608-3.0744-.298-4.9572.31-1.8748.949-3.5193,1.918-4.9333s2.209-2.5144,3.718-3.3008c1.51-.7865,3.222-1.1798,5.136-1.1798c1.923,0,3.507.3973,4.755,1.1917c1.255.7944,2.137,1.9026,2.645,3.3246.516,1.422.62,3.0784.31,4.9691-.31,1.8748-.953,3.5193-1.931,4.9333-.977,1.4062-2.216,2.5025-3.717,3.2889-1.502.7865-3.21,1.1798-5.124,1.1798Zm.655-4.3972c.715,0,1.354-.2144,1.919-.6434.571-.429,1.052-1.0288,1.441-1.7994.39-.7706.668-1.6643.835-2.6812.166-1.0009.182-1.8788.047-2.6335-.127-.7626-.397-1.3544-.81-1.7755-.413-.429-.973-.6435-1.68-.6435-.715,0-1.359.2185-1.931.6554-.572.429-1.052,1.0288-1.442,1.7994-.381.7626-.655,1.6564-.822,2.6812-.167,1.0009-.187,1.8788-.059,2.6335.127.7547.401,1.3465.822,1.7755.421.421.981.6315,1.68.6315Z"
                              transform="translate(.000001 0)"
                            />
                          </mask>
                        </g>
                      </g>
                    </g>
                    <g id="ejzKihZF9pk84_ts" transform="translate(213.739502,89.266453)">
                      <g transform="translate(-213.739502,-89.266453)" filter="url(#ejzKihZF9pk84-filter)">
                        <path
                          d="M178.906,89.575c0,2.0853.233,3.608.7,4.5683.466.9329,1.248,1.3994,2.346,1.3994c1.097,0,2.922-.3019,5.473-.9055l1.317,6.5438c-2.688.851-5.281,1.276-7.778,1.276-3.814,0-6.599-1.084-8.355-3.2514-1.728-2.195-2.593-5.4464-2.593-9.7541c0-4.3351.906-7.6413,2.717-9.9186c1.811-2.3047,4.76-3.4571,8.848-3.4571c2.387,0,4.774.4664,7.161,1.3993l-1.358,7.3258c-2.716-.7957-4.582-1.1935-5.597-1.1935-.988,0-1.715.4801-2.181,1.4404-.467.9329-.7,2.442-.7,4.5272Zm33.413,12.47h-8.643l-.823-3.9507h-4.569l-.864,3.9507h-8.272l5.309-25.5577h12.552l5.31,25.5577Zm-10.084-9.2598l-1.564-8.5605h-.164l-1.605,8.5605h3.333Zm11.338-8.1078c0-2.7163.823-4.829,2.469-6.338c1.646-1.5091,3.924-2.2636,6.832-2.2636c2.936,0,5.954.2744,9.055.8231l-.947,7.2435c-3.43-.6859-5.68-1.0289-6.75-1.0289s-1.605.3292-1.605.9877c0,.247.083.4253.247.5351.165.0823.288.1509.371.2057.082.0549.233.1235.452.2058.247.0823.439.1647.576.247.165.0548.659.2606,1.482.6173.851.3293,1.482.6036,1.893.8231.439.1921,1.029.5213,1.77.9878c2.14,1.3993,3.21,3.4571,3.21,6.1734s-.809,4.829-2.428,6.3376c-1.619,1.482-3.951,2.223-6.997,2.223-3.018,0-5.981-.371-8.889-1.111l.576-6.9557c3.676.9329,6.16,1.3993,7.449,1.3993.851,0,1.276-.3018,1.276-.9054c0-.2744-.11-.4939-.329-.6585-.192-.1647-.494-.343-.906-.5351-.384-.192-.96-.439-1.728-.7408-.769-.3292-1.413-.6036-1.935-.8231-.521-.2469-1.138-.6173-1.852-1.1112-2.195-1.5091-3.292-3.6218-3.292-6.3381Zm43.889,17.3676h-8.684v-9.0129h-4.445v9.0129h-8.807v-25.5577h8.807v9.5483h4.445v-9.5483h8.684v25.5577Z"
                          fill="url(#ejzKihZF9pk85-fill)"
                        />
                        <g mask="url(#ejzKihZF9pk88)">
                          <path
                            d="M179.606,94.1433l-1.322.6422.003.0076.004.0076l1.315-.6574Zm7.819.4939l1.441-.29-.299-1.4903-1.48.3499.338,1.4304Zm1.317,6.5438l.444,1.401l1.258-.397-.261-1.294-1.441.29Zm-16.133-1.9754l-1.155.9094.007.008.006.008l1.142-.9254Zm.124-19.6727l1.15.9148.006-.0067-1.156-.9081Zm16.009-2.0578l1.446.2679.221-1.1947-1.131-.4422-.536,1.369Zm-1.358,7.3258l-.413,1.4106l1.562.4575.297-1.6002-1.446-.2679Zm-7.778.2469l1.315.6574.003-.0076.004-.0076-1.322-.6422Zm-2.17,4.5272c0,2.1349.229,3.9378.848,5.2105l2.644-1.2843c-.315-.6479-.552-1.8905-.552-3.9262h-2.94Zm.855,5.2257c.34.6802.832,1.2573,1.498,1.6545.662.3945,1.403.5573,2.163.5573v-2.9397c-.338,0-.536-.0704-.657-.1423-.116-.0693-.248-.1918-.374-.4445l-2.63,1.3147Zm3.661,2.2118c1.286,0,3.26-.3412,5.812-.9449l-.677-2.8608c-2.551.6036-4.227.866-5.135.866v2.9397Zm4.033-2.0853l1.317,6.5438l2.881-.58-1.317-6.5438-2.881.58Zm2.314,4.8524c-2.564.8114-5.007,1.2074-7.335,1.2074v2.94c2.665,0,5.408-.454,8.222-1.345l-.887-2.8024Zm-7.335,1.2074c-3.543,0-5.829-.9984-7.213-2.7067l-2.284,1.8507c2.128,2.627,5.412,3.796,9.497,3.796v-2.94Zm-7.2-2.6908c-1.44-1.8286-2.278-4.6978-2.278-8.8447h-2.939c0,4.4686.89,8.102,2.907,10.6635l2.31-1.8188Zm-2.278-8.8447c0-4.1591.873-7.087,2.397-9.0038l-2.301-1.8296c-2.097,2.6378-3.035,6.3223-3.035,10.8334h2.939Zm2.403-9.0105c1.433-1.8245,3.867-2.8954,7.692-2.8954v-2.9397c-4.351,0-7.816,1.2339-10.004,4.0189l2.312,1.8162Zm7.692-2.8954c2.191,0,4.397.4273,6.626,1.2985l1.071-2.738c-2.545-.9946-5.113-1.5002-7.697-1.5002v2.9397Zm5.716-.3384l-1.358,7.3258l2.891.5358l1.358-7.3258-2.891-.5358Zm.501,6.1831c-2.692-.7885-4.754-1.2528-6.011-1.2528v2.9397c.773,0,2.443.3314,5.184,1.1343l.827-2.8212Zm-6.011-1.2528c-.742,0-1.461.1841-2.096.6034-.628.4149-1.085,1.0006-1.407,1.6647l2.644,1.2844c.144-.2962.284-.4307.383-.496.092-.0608.231-.1168.476-.1168v-2.9397Zm-3.496,2.253c-.627,1.2547-.855,3.0552-.855,5.1845h2.94c0-2.0412.239-3.2588.545-3.8698l-2.63-1.3147Zm34.028,17.6545v1.47h1.806l-.367-1.769-1.439.299Zm-8.643,0l-1.439.3.244,1.17h1.195v-1.47Zm-.823-3.9507l1.439-.2997-.244-1.1701h-1.195v1.4698Zm-4.569,0v-1.4698h-1.183l-.253,1.1557l1.436.3141Zm-.864,3.9507v1.47h1.183l.253-1.156-1.436-.314Zm-8.272,0l-1.44-.299-.367,1.769h1.807v-1.47Zm5.309-25.5577v-1.4698h-1.196l-.243,1.1709l1.439.2989Zm12.552,0l1.44-.2989-.244-1.1709h-1.196v1.4698Zm-4.774,16.2979v1.4699h1.763l-.317-1.7341-1.446.2642Zm-1.564-8.5605l1.446-.2642-.22-1.2057h-1.226v1.4699Zm-.164,0v-1.4699h-1.22l-.225,1.199l1.445.2709Zm-1.605,8.5605l-1.445-.2709-.326,1.7408h1.771v-1.4699Zm13.417,7.7898h-8.643v2.94h8.643v-2.94Zm-7.204,1.171l-.823-3.9514-2.878.5995.823,3.9509l2.878-.599Zm-2.262-5.1215h-4.569v2.9397h4.569v-2.9397Zm-6.005,1.1557l-.864,3.9508l2.872.628.864-3.9506-2.872-.6282Zm.572,2.7948h-8.272v2.94h8.272v-2.94Zm-6.833,1.769l5.309-25.5577-2.878-.5979-5.31,25.5576l2.879.598Zm3.87-24.3868h12.552v-2.9397h-12.552v2.9397Zm11.113-1.1709l5.309,25.5577l2.879-.598-5.309-25.5576-2.879.5979ZM203.681,92.521l-1.564-8.5605-2.892.5284l1.564,8.5605l2.892-.5284Zm-3.01-9.7662h-.164v2.9398h.164v-2.9398Zm-1.609,1.199l-1.605,8.5605l2.889.5418l1.605-8.5605-2.889-.5418Zm-.16,10.3013h3.333v-2.9398h-3.333v2.9398Zm17.14-15.9157l-.993-1.0835.993,1.0835Zm15.887-1.4405l1.457.1905.182-1.393-1.383-.2449-.256,1.4474Zm-.947,7.2435l-.288,1.4413l1.542.3084.203-1.5592-1.457-.1905Zm-8.108.4939l-.815,1.223.076.0507.082.041.657-1.3147Zm.371.2057l-.816,1.223v0l.816-1.223Zm.452.2058l-.516,1.3763.026.0096.025.0086.465-1.3945Zm.576.247l-.756,1.2604.139.083.153.051.464-1.3944Zm1.482.6173l-.584,1.3487.026.0116.027.0105.531-1.3708Zm1.893.8231l-.691,1.297.05.0268.052.0229.589-1.3467Zm1.77.9878l.804-1.2303-.01-.0068-.011-.0068-.783,1.2439Zm.782,12.511l.992,1.085.01-.009-1.002-1.076Zm-15.886,1.112l-1.465-.122-.103,1.239l1.205.307.363-1.424Zm.576-6.9557l.361-1.4247-1.683-.4271-.143,1.7304l1.465.1214Zm8.396-.1646l-.957,1.116.036.0311.039.0288.882-1.1759Zm-.906-.5351l-.657,1.3147.018.0089.018.0084.621-1.332Zm-1.728-.7408l-.579,1.351.02.0089.021.0082.538-1.3681Zm-1.935-.8231l-.629,1.3284.029.0138.03.0125.57-1.3547Zm-1.852-1.1112l.837-1.2085-.004-.0028-.833,1.2113Zm-1.822-6.3381c0-2.4006.714-4.0829,1.992-5.2545l-1.986-2.167c-2.014,1.8465-2.946,4.3895-2.946,7.4215h2.94Zm1.992-5.2545c1.302-1.1929,3.184-1.8773,5.839-1.8773v-2.9397c-3.162,0-5.834.8247-7.825,2.65l1.986,2.167Zm5.839-1.8773c2.844,0,5.776.2658,8.798.8007l.513-2.8948c-3.179-.5626-6.283-.8456-9.311-.8456v2.9397Zm7.597-.8372l-.947,7.2435l2.915.381.947-7.2435-2.915-.381Zm.799,5.9927c-3.396-.6793-5.795-1.0575-7.038-1.0575v2.9398c.898,0,2.998.3077,6.462,1.0003l.576-2.8826Zm-7.038-1.0575c-.638,0-1.364.0896-1.974.465-.727.4474-1.101,1.1828-1.101,1.9926h2.94c0,.0376-.009.1421-.077.268-.07.1289-.162.2067-.221.243-.049.0303-.054.0195.026.0029.08-.0162.211-.0317.407-.0317v-2.9398Zm-3.075,2.4576c0,.6002.225,1.307.902,1.7581l1.63-2.446c.142.0945.261.2318.335.3909.067.147.073.2609.073.297h-2.94Zm1.06,1.8498c.068.0343.121.0619.159.0833.041.0225.056.0324.053.0307l1.631-2.446c-.159-.106-.347-.2068-.529-.2974l-1.314,2.6294Zm.212.114c.237.1581.531.2763.752.3591l1.032-2.7525c-.09-.0339-.15-.0588-.185-.0751-.018-.0078-.024-.0111-.021-.0098.001.0007.021.0113.053.0323l-1.631,2.446Zm.803.3773c.094.0312.165.0582.216.08.052.0224.072.0344.069.0329l1.513-2.5208c-.268-.1609-.574-.2831-.868-.381l-.93,2.7889Zm.577.2469c-.003-.001.004.0014.026.0095.02.0076.047.0182.083.0322.071.0282.165.0666.284.1162.238.099.56.2365.969.4137l1.169-2.6974c-.792-.3429-1.36-.5826-1.602-.6631l-.929,2.7889Zm1.415.5937c.837.3239,1.402.5727,1.733.7493l1.383-2.5939c-.492-.2624-1.19-.5624-2.054-.8969l-1.062,2.7415Zm1.835.799c.338.1479.855.4312,1.576.8849l1.566-2.4877c-.761-.4792-1.424-.8543-1.964-1.0905l-1.178,2.6933Zm1.555.8713c1.694,1.1082,2.544,2.686,2.544,4.9432h2.94c0-3.1754-1.29-5.7132-3.876-7.4037l-1.608,2.4605Zm2.544,4.9432c0,2.4082-.706,4.0932-1.96,5.2629l2.004,2.1507c1.983-1.8488,2.896-4.3892,2.896-7.4136h-2.94Zm-1.95,5.2538c-1.259,1.1518-3.183,1.8368-6.005,1.8368v2.94c3.27,0,6.01-.796,7.989-2.608l-1.984-2.1688Zm-6.005,1.8368c-2.896,0-5.737-.355-8.527-1.0657l-.725,2.8487c3.027.771,6.113,1.157,9.252,1.157v-2.94Zm-7.424.48l.576-6.9554-2.93-.2427-.576,6.9551l2.93.243Zm-1.251-5.652c3.649.9257,6.308,1.4445,7.811,1.4445v-2.9398c-1.076,0-3.383-.4141-7.088-1.3541l-.723,2.8494Zm7.811,1.4445c.555,0,1.234-.0904,1.808-.4976.666-.4729.938-1.1858.938-1.8777h-2.94c0-.0212.004-.1102.06-.2288.059-.1262.149-.2261.24-.2909.082-.0582.135-.0668.114-.0619-.022.0051-.089.0171-.22.0171v2.9398Zm2.746-2.3753c0-.7371-.328-1.3922-.917-1.8344l-1.764,2.3518c-.051-.0383-.127-.1131-.186-.2305s-.073-.2232-.073-.2869h2.94Zm-.843-1.7745c-.343-.2945-.787-.5397-1.24-.751l-1.243,2.6639c.37.1728.53.2843.57.3191l1.913-2.232Zm-1.204-.7338c-.444-.222-1.07-.4881-1.849-.7942l-1.075,2.7362c.758.2975,1.285.5253,1.609.6874l1.315-2.6294Zm-1.807-.7771c-.77-.33-1.418-.6057-1.943-.8268l-1.141,2.7094c.517.2178,1.159.4909,1.926.8194l1.158-2.702Zm-1.884-.8005c-.423-.2002-.968-.5227-1.645-.9913l-1.673,2.417c.75.5191,1.439.9374,2.059,1.2311l1.259-2.6568Zm-1.649-.9941c-1.787-1.2284-2.655-2.883-2.655-5.1268h-2.94c0,3.1889,1.326,5.7596,3.93,7.5493l1.665-2.4225Zm39.764,12.2408v1.47h1.47v-1.47h-1.47Zm-8.684,0h-1.47v1.47h1.47v-1.47Zm0-9.0129h1.47v-1.4698h-1.47v1.4698Zm-4.445,0v-1.4698h-1.47v1.4698h1.47Zm0,9.0129v1.47h1.47v-1.47h-1.47Zm-8.807,0h-1.47v1.47h1.47v-1.47Zm0-25.5577v-1.4698h-1.47v1.4698h1.47Zm8.807,0h1.47v-1.4698h-1.47v1.4698Zm0,9.5483h-1.47v1.4698h1.47v-1.4698Zm4.445,0v1.4698h1.47v-1.4698h-1.47Zm0-9.5483v-1.4698h-1.47v1.4698h1.47Zm8.684,0h1.47v-1.4698h-1.47v1.4698Zm0,24.0877h-8.684v2.94h8.684v-2.94Zm-7.214,1.47v-9.0129h-2.94v9.0129h2.94Zm-1.47-10.4827h-4.445v2.9397h4.445v-2.9397Zm-5.915,1.4698v9.0129h2.94v-9.0129h-2.94Zm1.47,7.5429h-8.807v2.94h8.807v-2.94Zm-7.338,1.47v-25.5577h-2.939v25.5577h2.939Zm-1.469-24.0878h8.807v-2.9397h-8.807v2.9397Zm7.337-1.4699v9.5483h2.94v-9.5483h-2.94Zm1.47,11.0181h4.445v-2.9397h-4.445v2.9397Zm5.915-1.4698v-9.5483h-2.94v9.5483h2.94Zm-1.47-8.0784h8.684v-2.9397h-8.684v2.9397Zm7.214-1.4699v25.5577h2.94v-25.5577h-2.94Z"
                            fill="url(#ejzKihZF9pk87-fill)"
                          />
                          <mask
                            id="ejzKihZF9pk88"
                            // eslint-disable-next-line
                            mask-type="luminance"
                            x="-150%"
                            y="-150%"
                            height="400%"
                            width="400%"
                          >
                            <path
                              d="M178.906,89.575c0,2.0853.233,3.608.7,4.5683.466.9329,1.248,1.3994,2.346,1.3994c1.097,0,2.922-.3019,5.473-.9055l1.317,6.5438c-2.688.851-5.281,1.276-7.778,1.276-3.814,0-6.599-1.084-8.355-3.2514-1.728-2.195-2.593-5.4464-2.593-9.7541c0-4.3351.906-7.6413,2.717-9.9186c1.811-2.3047,4.76-3.4571,8.848-3.4571c2.387,0,4.774.4664,7.161,1.3993l-1.358,7.3258c-2.716-.7957-4.582-1.1935-5.597-1.1935-.988,0-1.715.4801-2.181,1.4404-.467.9329-.7,2.442-.7,4.5272Zm33.413,12.47h-8.643l-.823-3.9507h-4.569l-.864,3.9507h-8.272l5.309-25.5577h12.552l5.31,25.5577Zm-10.084-9.2598l-1.564-8.5605h-.164l-1.605,8.5605h3.333Zm11.338-8.1078c0-2.7163.823-4.829,2.469-6.338c1.646-1.5091,3.924-2.2636,6.832-2.2636c2.936,0,5.954.2744,9.055.8231l-.947,7.2435c-3.43-.6859-5.68-1.0289-6.75-1.0289s-1.605.3292-1.605.9877c0,.247.083.4253.247.5351.165.0823.288.1509.371.2057.082.0549.233.1235.452.2058.247.0823.439.1647.576.247.165.0548.659.2606,1.482.6173.851.3293,1.482.6036,1.893.8231.439.1921,1.029.5213,1.77.9878c2.14,1.3993,3.21,3.4571,3.21,6.1734s-.809,4.829-2.428,6.3376c-1.619,1.482-3.951,2.223-6.997,2.223-3.018,0-5.981-.371-8.889-1.111l.576-6.9557c3.676.9329,6.16,1.3993,7.449,1.3993.851,0,1.276-.3018,1.276-.9054c0-.2744-.11-.4939-.329-.6585-.192-.1647-.494-.343-.906-.5351-.384-.192-.96-.439-1.728-.7408-.769-.3292-1.413-.6036-1.935-.8231-.521-.2469-1.138-.6173-1.852-1.1112-2.195-1.5091-3.292-3.6218-3.292-6.3381Zm43.889,17.3676h-8.684v-9.0129h-4.445v9.0129h-8.807v-25.5577h8.807v9.5483h4.445v-9.5483h8.684v25.5577Z"
                              fill="#fff"
                            />
                          </mask>
                        </g>
                      </g>
                    </g>
                    <g>
                      <g id="ejzKihZF9pk91_to" transform="translate(178.071999,120.475014)">
                        <g id="ejzKihZF9pk91_ts">
                          <g transform="translate(-196.071999,-120.475014)" filter="url(#ejzKihZF9pk91-filter)">
                            <image
                              width="164"
                              height="164"
                              xlinkHref={StarImage}
                              preserveAspectRatio="xMidYMid meet"
                              transform="matrix(.152892 0 0 0.152892 182.936199 109.002029)"
                            />
                          </g>
                        </g>
                      </g>
                      <g id="ejzKihZF9pk93_to" transform="translate(196.071999,120.475014)">
                        <g id="ejzKihZF9pk93_ts">
                          <g transform="translate(-196.071999,-120.475014)" filter="url(#ejzKihZF9pk93-filter)">
                            <image
                              width="164"
                              height="164"
                              xlinkHref={StarImage}
                              preserveAspectRatio="xMidYMid meet"
                              transform="matrix(.152892 0 0 0.152892 183.534863 108.964176)"
                            />
                          </g>
                        </g>
                      </g>
                      <g id="ejzKihZF9pk95_to" transform="translate(213.999504,120.501442)">
                        <g id="ejzKihZF9pk95_ts">
                          <g transform="translate(-213.999504,-120.501442)" filter="url(#ejzKihZF9pk95-filter)">
                            <image
                              width="164"
                              height="164"
                              xlinkHref={StarImage}
                              preserveAspectRatio="xMidYMid meet"
                              transform="matrix(.152892 0 0 0.152892 201.533773 109.002029)"
                            />
                          </g>
                        </g>
                      </g>
                      <g id="ejzKihZF9pk97_to" transform="translate(231.927498,120.475014)">
                        <g id="ejzKihZF9pk97_ts">
                          <g transform="translate(-231.927498,-120.475014)" filter="url(#ejzKihZF9pk97-filter)">
                            <image
                              width="164"
                              height="164"
                              xlinkHref={StarImage}
                              preserveAspectRatio="xMidYMid meet"
                              transform="matrix(.152892 0 0 0.152892 219.209758 108.964176)"
                            />
                          </g>
                        </g>
                      </g>
                      <g id="ejzKihZF9pk99_to" transform="translate(250.473343,120.475014)">
                        <g id="ejzKihZF9pk99_ts">
                          <g transform="translate(-250.473343,-120.475014)" filter="url(#ejzKihZF9pk99-filter)">
                            <image
                              width="164"
                              height="164"
                              xlinkHref={StarImage}
                              preserveAspectRatio="xMidYMid meet"
                              transform="matrix(.152892 0 0 0.152892 237.936199 108.964176)"
                            />
                          </g>
                        </g>
                      </g>
                    </g>
                    <g id="ejzKihZF9pk101_to" transform="translate(172.268524,94.958542)">
                      <g id="ejzKihZF9pk101_ts">
                        <g transform="translate(-158.268524,-82.958542)">
                          <g filter="url(#ejzKihZF9pk102-filter)">
                            <path
                              d="M147.305,82.722c5.42,8.4211,14.119,12.9428,18.119,14.2628.164.054.338.0402.494-.0326.389-.1819.519-.6714.271-1.0219l-8.241-11.6288c-.077-.1096-.144-.2267-.198-.3496l-5.979-13.5826c-.189-.4292-.615-.7054-1.085-.703-.545.0029-1.017.3789-1.14.909l-2.488,10.6656c-.118.5072-.035,1.0428.247,1.4811Z"
                              fill="url(#ejzKihZF9pk103-fill)"
                            />
                          </g>
                          <g filter="url(#ejzKihZF9pk104-filter)">
                            <path
                              d="M148.25,80.9813l2.885-11.8764c.058-.2403.418-.2798.541-.0653.772,1.3421,2.653,4.0113,6.214,7.7797c4.206,4.4508,9.248,6.8145,11.458,7.5183.125.0398.202.1649.179.2936L167.36,96.5511c-.025.1386-.156.2306-.292.193-1.122-.3093-4.2-1.6454-9.466-5.1261-5.65-3.7351-8.559-8.4985-9.343-10.488-.019-.048-.021-.0985-.009-.1487Z"
                              fill="url(#ejzKihZF9pk105-fill)"
                            />
                          </g>
                          <g filter="url(#ejzKihZF9pk106-filter)">
                            <path
                              d="M152.607,73.4841c.922.4132,1.232-.277,1.272-.6737.788.8139,3.137,3.1205,6.235,5.8363s5.554,3.9646,6.395,4.2494c-.764,1.2057.409,2.5792,1.09,3.1153l-1.175,5.6926c-1.272-.1278-1.816.7527-1.928,1.2089-.384-.1722-2.165-1.2195-6.217-4.0311-4.051-2.8117-5.937-5.4725-6.373-6.4514.796-1.523.015-2.8037-.475-3.2537l1.176-5.6926Z"
                              fill="#d8f3db"
                            />
                          </g>
                          <ellipse
                            rx="3.58359"
                            ry="2.10999"
                            transform="matrix(.199187 0.979326-.982788 0.188123 159.742131 83.50444)"
                            fill="#26782e"
                          />
                          <g filter="url(#ejzKihZF9pk109-filter)">
                            <ellipse
                              rx="3.35805"
                              ry="1.83079"
                              transform="matrix(.199187 0.979326-.982788 0.188123 159.465601 83.558039)"
                              fill="url(#ejzKihZF9pk110-fill)"
                            />
                          </g>
                        </g>
                      </g>
                    </g>
                    <g style={{ mixBlendMode: 'screen' }}>
                      <g id="ejzKihZF9pk112_to" transform="translate(178.071999,120.475014)">
                        <g id="ejzKihZF9pk112_ts">
                          <g
                            id="ejzKihZF9pk112"
                            transform="translate(-196.071999,-120.475014)"
                            filter="url(#ejzKihZF9pk112-filter)"
                          >
                            <image
                              width="164"
                              height="164"
                              xlinkHref={StarImage}
                              preserveAspectRatio="xMidYMid meet"
                              transform="matrix(.152892 0 0 0.152892 182.936199 109.002029)"
                            />
                          </g>
                        </g>
                      </g>
                      <g id="ejzKihZF9pk114_to" transform="translate(196.071999,120.475014)">
                        <g id="ejzKihZF9pk114_ts">
                          <g
                            id="ejzKihZF9pk114"
                            transform="translate(-196.071999,-120.475014)"
                            filter="url(#ejzKihZF9pk114-filter)"
                          >
                            <image
                              width="164"
                              height="164"
                              xlinkHref={StarImage}
                              preserveAspectRatio="xMidYMid meet"
                              transform="matrix(.152892 0 0 0.152892 183.534863 108.964176)"
                            />
                          </g>
                        </g>
                      </g>
                      <g id="ejzKihZF9pk116_to" transform="translate(213.999504,120.501442)">
                        <g id="ejzKihZF9pk116_ts">
                          <g
                            id="ejzKihZF9pk116"
                            transform="translate(-213.999504,-120.501442)"
                            filter="url(#ejzKihZF9pk116-filter)"
                          >
                            <image
                              width="164"
                              height="164"
                              xlinkHref={StarImage}
                              preserveAspectRatio="xMidYMid meet"
                              transform="matrix(.152892 0 0 0.152892 201.533773 109.002029)"
                            />
                          </g>
                        </g>
                      </g>
                      <g id="ejzKihZF9pk118_to" transform="translate(231.927498,120.475014)">
                        <g id="ejzKihZF9pk118_ts">
                          <g
                            id="ejzKihZF9pk118"
                            transform="translate(-231.927498,-120.475014)"
                            filter="url(#ejzKihZF9pk118-filter)"
                          >
                            <image
                              width="164"
                              height="164"
                              xlinkHref={StarImage}
                              preserveAspectRatio="xMidYMid meet"
                              transform="matrix(.152892 0 0 0.152892 219.209758 108.964176)"
                            />
                          </g>
                        </g>
                      </g>
                      <g id="ejzKihZF9pk120_to" transform="translate(250.473343,120.475014)">
                        <g id="ejzKihZF9pk120_ts">
                          <g
                            id="ejzKihZF9pk120"
                            transform="translate(-250.473343,-120.475014)"
                            filter="url(#ejzKihZF9pk120-filter)"
                          >
                            <image
                              width="164"
                              height="164"
                              xlinkHref={StarImage}
                              preserveAspectRatio="xMidYMid meet"
                              transform="matrix(.152892 0 0 0.152892 237.936199 108.964176)"
                            />
                          </g>
                        </g>
                      </g>
                    </g>
                    <g id="ejzKihZF9pk122_to" transform="translate(261.077072,59.556232)">
                      <g id="ejzKihZF9pk122_ts">
                        <g transform="translate(-271.077072,-51.556232)">
                          <g filter="url(#ejzKihZF9pk123-filter)">
                            <path
                              d="M281.819,49.5198c-3.993,9.1814-11.85,15.0405-15.587,16.9857-.153.0796-.327.0939-.492.0471-.414-.1171-.621-.5795-.432-.9655l6.26-12.7999c.059-.1207.106-.247.14-.377l3.714-14.3652c.118-.4543.494-.7956.958-.8686.538-.0847,1.064.2105,1.272.7138l4.172,10.1278c.199.4817.203,1.0238-.005,1.5018Z"
                              fill="url(#ejzKihZF9pk124-fill)"
                            />
                          </g>
                          <g filter="url(#ejzKihZF9pk125-filter)">
                            <path
                              d="M280.606,47.9534l-4.759-11.2589c-.096-.2278-.458-.2089-.545.0226-.546,1.4486-1.972,4.385-4.88,8.6761-3.435,5.068-8.03,8.2102-10.099,9.2598-.116.0593-.172.1952-.128.3185l4.057,11.417c.047.1328.192.2025.32.1436c1.057-.4853,3.88-2.2982,8.517-6.579c4.975-4.5936,7.08-9.762,7.532-11.8514.011-.0505.006-.1007-.015-.1483Z"
                              fill="url(#ejzKihZF9pk126-fill)"
                            />
                          </g>
                          <g filter="url(#ejzKihZF9pk127-filter)">
                            <path
                              d="M275.099,41.2527c-.843.5558-1.26-.0756-1.364-.4607-.646.9297-2.594,3.5835-5.214,6.7613s-4.843,4.8045-5.627,5.2207c.948,1.0673.012,2.6112-.575,3.2498l2.077,5.4298c1.235-.3303,1.913.4514,2.098.8836.351-.2316,1.94-1.5511,5.486-4.9765c3.546-3.4255,4.979-6.3544,5.252-7.3906-1.031-1.3755-.466-2.7649-.055-3.2876l-2.078-5.4298Z"
                              fill="#d8f3db"
                            />
                          </g>
                          <ellipse
                            rx="3.58312"
                            ry="2.11026"
                            transform="matrix(-.038902 0.998692 1.00017 0.027903 269.670228 52.288014)"
                            fill="#26782e"
                          />
                          <g filter="url(#ejzKihZF9pk130-filter)">
                            <ellipse
                              rx="3.35761"
                              ry="1.83103"
                              transform="matrix(-.038902 0.998692 1.00017 0.027903 269.950723 52.296108)"
                              fill="url(#ejzKihZF9pk131-fill)"
                            />
                          </g>
                        </g>
                      </g>
                    </g>
                  </g>
                </g>
              </g>
            </g>
            <clipPath id="ejzKihZF9pk132">
              <rect width="700" height="500" rx="16" ry="16" transform="translate(20 10)" fill="#fff" />
            </clipPath>
          </g>
        </g>
      </svg>
    </div>
  );
};

export default MomentumWinAnimated;
